import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { TrendingUp, Users, Package, ShoppingCart, Calendar, Filter, Eye, X, Building2, Euro, Hash, Box, Award, ChevronDown, ChevronUp, FileText, ArrowLeft, Search, BarChart3, Activity, Target, Layers, CheckSquare, Plus, Move, Maximize2, Minimize2, Copy, Trash2, Settings, Grid, List, ChevronRight, ChevronLeft, Loader, UserCheck, Download, FileSpreadsheet, Image, Save, FolderOpen } from 'lucide-react';
import { useTheme } from "../contexts/ThemeContext";

const AuthContext = React.createContext();
const API_URL = process.env.REACT_APP_API_URL;

// Helper para convertir hex a rgba
const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(60,191,154,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Cache en memoria para evitar llamadas repetidas
const apiCache = new Map();
const CACHE_DURATION = 3 * 60 * 1000; // 3 minutos

// Helper para cache
const getCachedData = (key) => {
  const cached = apiCache.get(key);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  apiCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Helper para debounce
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Helper para pausas entre llamadas
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Función para exportar widget individual como PNG
const exportWidgetAsPNG = async (widgetId, title) => {
  try {
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId}"]`);
    if (!widgetElement) {
      throw new Error('Widget no encontrado');
    }

    if (window.html2canvas) {
      const canvas = await window.html2canvas(widgetElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: widgetElement.offsetWidth,
        height: widgetElement.offsetHeight
      });
      
      const link = document.createElement('a');
      link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      script.onload = async () => {
        const canvas = await window.html2canvas(widgetElement, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: widgetElement.offsetWidth,
          height: widgetElement.offsetHeight
        });
        
        const link = document.createElement('a');
        link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      script.onerror = () => {
        throw new Error('No se pudo cargar html2canvas');
      };
      document.head.appendChild(script);
      return true;
    }
  } catch (error) {
    console.error('Error al exportar widget:', error);
    return false;
  }
};

// Modal para guardar configuración
const SaveConfigModal = ({ isOpen, onClose, onSave, isLoading }) => {
  const { theme } = useTheme();
  const [nombre, setNombre] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nombre.trim()) {
      onSave(nombre.trim());
      setNombre('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Guardar Configuración</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de la configuración
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': theme.primary }}
              placeholder="Ej: Análisis Q1 2024"
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!nombre.trim() || isLoading}
              className="px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center transition-colors duration-200"
              style={{ backgroundColor: theme.primary }}
              onMouseEnter={e => { if (!isLoading && nombre.trim()) e.currentTarget.style.backgroundColor = theme.primaryDark; }}
              onMouseLeave={e => { if (!isLoading && nombre.trim()) e.currentTarget.style.backgroundColor = theme.primary; }}
            >
              {isLoading ? (
                <>
                  <Loader size={16} className="mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Guardar
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Tooltip personalizado ordenado por valor
const ETIQUETAS_SUBMETRICA_PRODUCTO = {
  total_vendido: 'Unidades vendidas',
  numero_pedidos: 'Nº Pedidos',
  facturacion_total: 'Facturación',
  clientes_distintos: 'Clientes distintos'
};

const CustomTooltip = ({ active, payload, label, metricType, formatCurrency, formatNumber, submetricaProductos }) => {
  if (active && payload && payload.length) {
    const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));

    // Recharts ignora labelFormatter cuando el tooltip usa `content` propio, así
    // que el nombre sin truncar se toma del dato en vez del label del eje.
    const titulo = payload[0]?.payload?.nombreCompleto || label;

    // En modo productos el dataKey es una función, por lo que Recharts no puede
    // deducir `entry.name` y quedaría "​: 60" sin indicar qué se está midiendo.
    const nombreSerie = metricType === 'productos'
      ? (ETIQUETAS_SUBMETRICA_PRODUCTO[submetricaProductos] || 'Valor')
      : null;

    const esImporte = metricType === 'total' ||
      (metricType === 'productos' && submetricaProductos === 'facturacion_total');

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-800 mb-2">{titulo}</p>
        {sortedPayload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
            {entry.name || nombreSerie}: {esImporte ? formatCurrency(entry.value) : formatNumber(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Componente para el panel lateral deslizable
const SidePanel = ({ 
  isOpen, 
  setIsOpen,
  matrizProveedores, 
  listaClientes,  
  selectedWidget,
  widgets,
  updateWidget,
  busquedaProveedor, 
  setBusquedaProveedor,
  busquedaCliente,
  setBusquedaCliente,
  getProveedorColor,
  estadoFiltro,
  setEstadoFiltro,
  fechaDesde,
  setFechaDesde,
  fechaHasta,
  setFechaHasta,
  aplicarFiltros,
  formatCurrency,
  busquedaProducto,
  setBusquedaProducto,
  isApplyingFilters,
  user,
  setError,
  esClinicaMatriz,
  clientesAsociados,
  clientesMatrizSeleccionados,
  setClientesMatrizSeleccionados,
  isLoadingClientesMatriz
}) => {
  const { theme } = useTheme();
  const currentWidget = widgets.find(w => w.id === selectedWidget);
  
  const isAdmin = user?.permisos === 1;
  const isClient = user?.permisos === 5;
  const isProveedor = user?.permisos === 4;

  const getClientesParaFiltrado = useCallback(() => {
    if (isClient && esClinicaMatriz && clientesAsociados.length > 0) {
      const clientesReales = listaClientes.filter(cliente => 
        cliente.id === user?.clinica_info?.id || 
        clientesAsociados.some(asociado => asociado.id === cliente.id)
      );
      
      if (clientesReales.length !== (getClientesParaFiltrado._lastCount || 0)) {
        console.log('Clientes matriz con datos reales:', clientesReales.length, clientesReales.map(c => ({ 
          id: c.id, 
          nombre: c.razon_social, 
          pedidos: c.pedidos_activos, 
          facturado: c.total_facturado 
        })));
        getClientesParaFiltrado._lastCount = clientesReales.length;
      }
      
      return clientesReales;
    }
    return listaClientes;
  }, [isClient, esClinicaMatriz, clientesAsociados, listaClientes, user?.clinica_info?.id]);

  const debugClientesMatrix = () => {
    console.log('=== DEBUG CLIENTES MATRIZ ===');
    console.log('esClinicaMatriz:', esClinicaMatriz);
    console.log('clientesAsociados:', clientesAsociados.length);
    console.log('listaClientes:', listaClientes.length);
    console.log('user?.clinica_info:', user?.clinica_info);
    
    if (isClient && esClinicaMatriz) {
      console.log('LISTA CLIENTES ACTUAL:');
      listaClientes.forEach(cliente => {
        console.log(`  - ${cliente.razon_social}: ${cliente.pedidos_activos} pedidos, €${cliente.total_facturado}`);
      });
    }
    console.log('=== FIN DEBUG ===');
  };

  useEffect(() => {
    if (isClient && esClinicaMatriz) {
      debugClientesMatrix();
    }
  }, [listaClientes, esClinicaMatriz, clientesAsociados]);
    
  const proveedoresFiltrados = matrizProveedores.filter(proveedor =>
    proveedor.nombre.toLowerCase().includes(busquedaProveedor.toLowerCase())
  );

  const clientesFiltrados = getClientesParaFiltrado().filter(cliente =>
    cliente.razon_social.toLowerCase().includes(busquedaCliente.toLowerCase())
  );

  const toggleProveedorEnWidget = (proveedor) => {
    if (!currentWidget) return;
    
    const existe = currentWidget.proveedoresSeleccionados.find(p => p.id === proveedor.id);
    let nuevosProveedores;
    
    if (existe) {
      nuevosProveedores = currentWidget.proveedoresSeleccionados.filter(p => p.id !== proveedor.id);
    } else {
      nuevosProveedores = [...currentWidget.proveedoresSeleccionados, proveedor];
    }
    
    updateWidget(currentWidget.id, { proveedoresSeleccionados: nuevosProveedores });
  };

  const handleProveedorChange = (proveedor, accion) => {
    if (!currentWidget) return;
    
    let nuevosProveedores;
    const existe = currentWidget.proveedoresSeleccionados.find(p => p.id === proveedor?.id);
    
    if (accion === 'toggle') {
      if (existe) {
        nuevosProveedores = currentWidget.proveedoresSeleccionados.filter(p => p.id !== proveedor.id);
      } else {
        nuevosProveedores = [...currentWidget.proveedoresSeleccionados, proveedor];
      }
    } else if (accion === 'selectAll') {
      nuevosProveedores = currentWidget.proveedoresSeleccionados.length === matrizProveedores.length ?
        [] : [...matrizProveedores];
    }
    
    let nuevoTipoEje = currentWidget.tipoEje;
    if (nuevosProveedores.length !== 1 && currentWidget.tipoEje === 'cliente') {
      nuevoTipoEje = 'proveedor';
    }
    
    updateWidget(currentWidget.id, { 
      proveedoresSeleccionados: nuevosProveedores,
      tipoEje: nuevoTipoEje
    });
  };

  const seleccionarTodosProveedoresWidget = () => {
    if (!currentWidget) return;
    
    if (currentWidget.proveedoresSeleccionados.length === matrizProveedores.length) {
      updateWidget(currentWidget.id, { proveedoresSeleccionados: [] });
    } else {
      updateWidget(currentWidget.id, { proveedoresSeleccionados: [...matrizProveedores] });
    }
  };

  const toggleClienteWidget = (cliente) => {
    if (!currentWidget) return;
    
    const existe = currentWidget.clientesSeleccionados.find(c => c.id === cliente.id);
    let nuevosClientes;
    
    if (existe) {
      nuevosClientes = currentWidget.clientesSeleccionados.filter(c => c.id !== cliente.id);
    } else {
      nuevosClientes = [...currentWidget.clientesSeleccionados, cliente];
    }
    
    updateWidget(currentWidget.id, { clientesSeleccionados: nuevosClientes });
  };

  const seleccionarTodosClientesWidget = () => {
    if (!currentWidget) return;
    
    if (currentWidget.clientesSeleccionados.length === listaClientes.length) {
      updateWidget(currentWidget.id, { clientesSeleccionados: [] });
    } else {
      updateWidget(currentWidget.id, { clientesSeleccionados: [...listaClientes] });
    }
  };

  const cambiarMetricaWidget = (metrica) => {
    if (!currentWidget) return;
    
    updateWidget(currentWidget.id, { metricType: metrica });
  };

  const cambiarTipoGraficaWidget = (tipo) => {
    if (!currentWidget) return;
    updateWidget(currentWidget.id, { chartType: tipo });
  };

  const cambiarSubmetricaWidget = (submetrica) => {
    if (!currentWidget) return;
    updateWidget(currentWidget.id, { submetricaProductos: submetrica });
  };

  const cambiarTipoAnalisisWidget = (tipo) => {
    if (!currentWidget) return;
    updateWidget(currentWidget.id, { tipoAnalisis: tipo });
  };

  const cambiarTipoEjeWidget = (tipoEje) => {
    if (!currentWidget) return;
    updateWidget(currentWidget.id, { tipoEje: tipoEje });
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 top-1/2 transform -translate-y-1/2 z-50 bg-white border border-gray-300 rounded-r-lg p-3 shadow-lg hover:bg-gray-50 transition-all duration-300"
        style={{
          left: isOpen ? '384px' : '4px'
        }}
      >
        {isOpen ? <ChevronLeft size={20} className="text-gray-600" /> : <ChevronRight size={20} className="text-gray-600" />}
      </button>

      {/* Side Panel */}
      <div 
        className="fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 shadow-lg transition-transform duration-300 z-40 overflow-hidden"
        style={{
          width: '380px',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        <div className="h-full flex flex-col">
          
          {/* Panel Header */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-800">
                {currentWidget ? `Configurar: ${currentWidget.title}` : 'Panel de Control'}
              </h2>
              {currentWidget && (
                <div className="text-sm text-gray-500">
                  {!isProveedor && (
                    <span>{currentWidget.proveedoresSeleccionados.length} prov.</span>
                  )}
                  {(currentWidget.clientesSeleccionados?.length > 0 || (isClient && esClinicaMatriz && clientesMatrizSeleccionados.length > 0)) && (
                    <span>
                      {!isProveedor && ' • '}
                      {isClient && esClinicaMatriz ? 
                        `${clientesMatrizSeleccionados.length} clínicas` :
                        `${currentWidget.clientesSeleccionados?.length || 0} clientes`
                      }
                    </span>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {currentWidget ? 'Personaliza esta tarjeta' : '⚠️ Selecciona una tarjeta y aplica filtros'}
              {isClient && esClinicaMatriz && (
                <span className="block text-xs mt-1" style={{ color: theme.primary }}>
                  🏢 Clínica Matriz: Gestiona {clientesAsociados.length} clínicas asociadas
                </span>
              )}
            </p>
          </div>

          {/* FILTROS GLOBALES */}
          <div
            className="p-4 border-b-2"
            style={{
              borderBottomColor: hexToRgba(theme.primary, 0.3),
              background: `linear-gradient(to right, ${hexToRgba(theme.primary, 0.05)}, ${hexToRgba(theme.primary, 0.08)})`
            }}
          >
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Estado de Pedidos</label>
                <select
                  value={estadoFiltro}
                  onChange={(e) => setEstadoFiltro(e.target.value)}
                  className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': theme.primary }}
                >
                  <option value="">Todos los estados</option>
                  <option value="2">Pendiente</option>
                  <option value="3">En proceso</option>
                  <option value="4">Enviado</option>
                  <option value="5">Cerrado</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Desde</label>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': theme.primary }}
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Hasta</label>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': theme.primary }}
                  />
                </div>
              </div>
              
              <button
                onClick={aplicarFiltros}
                disabled={isApplyingFilters}
                className="w-full py-3 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ 
                  backgroundColor: isApplyingFilters ? '#6b7280' : theme.primary,
                  transform: isApplyingFilters ? 'scale(0.98)' : 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  if (!isApplyingFilters) {
                    e.target.style.backgroundColor = theme.primaryDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isApplyingFilters) {
                    e.target.style.backgroundColor = theme.primary;
                  }
                }}
              >
                {isApplyingFilters ? (
                  <>
                    <Loader size={16} className="mr-2 animate-spin" />
                    Aplicando filtros...
                  </>
                ) : (
                  <>
                    <Filter size={16} className="mr-2" />
                    Aplicar Filtros de Fecha/Estado
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center">
                Los demás cambios se aplican automáticamente
              </p>
            </div>
          </div>

          {currentWidget ? (
            <div className="flex-1 overflow-y-auto">

              {/* Configuración del Widget */}
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Settings size={16} className="mr-2" />
                  Configuración de tarjeta
                </h3>
                
                <div className="space-y-3">
                  {/* Selector de Métrica */}
                  <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Métrica Principal</label>
                  
                  {currentWidget.metricType === 'productos' && (
                    <div className="mb-2 p-2 rounded-lg border" style={{ backgroundColor: hexToRgba(theme.primary, 0.05), borderColor: hexToRgba(theme.primary, 0.3) }}>
                      <p className="text-xs" style={{ color: theme.primaryDark }}>
                        <span className="font-medium">📦 Modo Productos:</span> <br />
                        {busquedaProducto && busquedaProducto.length >= 3
                          ? `Buscando "${busquedaProducto}" en ${currentWidget.proveedoresSeleccionados.length || 'todos los'} proveedor(es).`
                          : currentWidget.proveedoresSeleccionados.length === 0
                            ? 'TOP 20 productos de todos tus proveedores. Selecciona proveedores para acotar.'
                            : `TOP 20 productos de los ${currentWidget.proveedoresSeleccionados.length} proveedor(es) seleccionado(s).`}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => cambiarMetricaWidget('total')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 ${
                        currentWidget.metricType === 'total' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                      }`}
                    >
                      Facturación
                    </button>
                    <button
                      onClick={() => cambiarMetricaWidget('pedidos')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 ${
                        currentWidget.metricType === 'pedidos' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                      }`}
                    >
                      Nº Pedidos
                    </button>
                    <button
                      onClick={() => cambiarMetricaWidget('articulos')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 ${
                        currentWidget.metricType === 'articulos' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                      }`}
                    >
                      Cantidades
                    </button>
                    <button
                      onClick={() => cambiarMetricaWidget('productos')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 ${
                        currentWidget.metricType === 'productos' ? 'bg-orange-100 border-orange-500 text-orange-700' :
                        'bg-gray-50 border-gray-300 text-gray-600'
                      }`}
                      title="Análisis detallado de productos"
                    >
                      Productos
                    </button>
                  </div>
                </div>

                  {/* Selector de Tipo de Gráfica */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Tipo de Gráfica</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => cambiarTipoGraficaWidget('line')}
                        className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 flex items-center justify-center ${
                          currentWidget.chartType === 'line' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <Activity size={14} className="mr-1" />
                        Línea
                      </button>
                      <button
                        onClick={() => cambiarTipoGraficaWidget('bar')}
                        className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 flex items-center justify-center ${
                          currentWidget.chartType === 'bar' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <BarChart3 size={14} className="mr-1" />
                        Barras
                      </button>
                      <button
                        onClick={() => cambiarTipoGraficaWidget('pie')}
                        className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 flex items-center justify-center ${
                          currentWidget.chartType === 'pie' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <Target size={14} className="mr-1" />
                        Circular
                      </button>
                      <button
                        onClick={() => cambiarTipoGraficaWidget('area')}
                        className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 flex items-center justify-center ${
                          currentWidget.chartType === 'area' ? 'bg-orange-100 border-orange-500 text-orange-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                      >
                        <Layers size={14} className="mr-1" />
                        Área
                      </button>
                    </div>
                  </div>

                  {/* Selector de Tipo de Eje X */}
                  {((isAdmin || isProveedor) || (isClient && esClinicaMatriz)) && currentWidget.metricType !== 'productos' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Comparar por</label>
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          onClick={() => cambiarTipoEjeWidget('proveedor')}
                          className="px-3 py-2 text-xs rounded-lg border transition-colors duration-200 flex items-center justify-center"
                          style={
                            (currentWidget.tipoEje || 'proveedor') === 'proveedor'
                              ? { backgroundColor: hexToRgba(theme.primary, 0.1), borderColor: theme.primary, color: theme.primaryDark }
                              : {}
                          }
                        >
                          <Building2 size={14} className="mr-1" />
                          {isProveedor ? 'Total por Fechas' : 'Por Proveedor'}
                        </button>
                        
                        {(
                          (isAdmin && currentWidget.proveedoresSeleccionados?.length === 1 && currentWidget.clientesSeleccionados?.length > 0) ||
                          (isProveedor && currentWidget.clientesSeleccionados?.length > 1) ||
                          (isClient && esClinicaMatriz && currentWidget.clientesSeleccionados?.length > 1 && currentWidget.proveedoresSeleccionados?.length >= 1)
                        ) && (
                          <button
                            onClick={() => cambiarTipoEjeWidget('cliente')}
                            className={`px-3 py-2 text-xs rounded-lg border transition-colors duration-200 flex items-center justify-center ${
                              currentWidget.tipoEje === 'cliente' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-gray-50 border-gray-300 text-gray-600'
                            }`}
                          >
                            <UserCheck size={14} className="mr-1" />
                            {isClient && esClinicaMatriz ? 
                              `Por Clínica (${currentWidget.clientesSeleccionados.length})` :
                              `Por Cliente (${currentWidget.clientesSeleccionados.length})`
                            }
                          </button>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {(currentWidget.tipoEje || 'proveedor') === 'proveedor' ? 
                          (isProveedor ? 'Evolución temporal total del proveedor' : 
                          isClient && esClinicaMatriz ? 'Comparar diferentes proveedores seleccionados' :
                          'Comparar diferentes proveedores seleccionados') : 
                          (isClient && esClinicaMatriz ? 
                            `Desglose por clínica con ${currentWidget.proveedoresSeleccionados.length} proveedor(es)` :
                            isProveedor ? 
                              `Desglose por cliente del proveedor` :
                              `Desglose por cliente del proveedor: ${currentWidget.proveedoresSeleccionados[0]?.nombre}`
                          )
                        }
                      </div>
                    </div>
                  )}

                  {/* Búsqueda de un producto concreto (opcional) en modo Productos */}
                  {currentWidget.metricType === 'productos' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Buscar Producto (opcional)</label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Buscar producto (ej: GASA)..."
                          value={busquedaProducto}
                          onChange={(e) => setBusquedaProducto(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': theme.primary }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Vacío = TOP 20 más vendidos. Mín. 3 letras para buscar uno concreto.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* SECCIÓN: Selección de Clientes */}
              {((isAdmin || isProveedor) || (isClient && esClinicaMatriz)) && (
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <UserCheck size={16} className="mr-2" />
                    {isProveedor ? 'Analizar por Clientes (opcional)' : 
                    isClient && esClinicaMatriz ? `Mis Clínicas (${clientesAsociados.length + 1} total)` :
                    'Clientes específicos (opcional)'}
                  </h3>

                  {isLoadingClientesMatriz && isClient && esClinicaMatriz ? (
                    <div className="text-center py-4">
                      <Loader size={20} className="animate-spin mx-auto" style={{ color: theme.primary }} />
                      <p className="text-sm mt-2" style={{ color: theme.primary }}>Cargando clínicas asociadas...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="relative mb-3">
                        <input
                          type="text"
                          placeholder={isClient && esClinicaMatriz ? "Buscar clínica..." : "Buscar cliente..."}
                          value={busquedaCliente}
                          onChange={(e) => setBusquedaCliente(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': theme.primary }}
                        />
                      </div>
                      
                      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                        {clientesFiltrados.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {isClient && esClinicaMatriz ? 
                              'No se encontraron clínicas asociadas' : 
                              'No se encontraron clientes'
                            }
                          </div>
                        ) : (
                          clientesFiltrados.map((cliente, index) => (
                            <div
                              key={cliente.id}
                              className="p-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors duration-200 hover:bg-gray-50"
                              style={
                                currentWidget.clientesSeleccionados?.some(c => c.id === cliente.id)
                                  ? { backgroundColor: hexToRgba(theme.primary, 0.06) }
                                  : {}
                              }
                              onClick={() => toggleClienteWidget(cliente)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center flex-1 min-w-0">
                                  <div 
                                    className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                                    style={{ backgroundColor: getProveedorColor(index) }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                      {cliente.razon_social}
                                    </h4>
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                      <span>{formatCurrency(cliente.total_facturado || 0)}</span>
                                      <span>{cliente.pedidos_activos || 0} pedidos</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <input
                                  type="checkbox"
                                  checked={currentWidget.clientesSeleccionados?.some(c => c.id === cliente.id) || false}
                                  onChange={() => toggleClienteWidget(cliente)}
                                  className="ml-2"
                                  style={{ accentColor: theme.primary }}
                                />
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      
                      {/* Botón para seleccionar todos los clientes (Admin/Proveedor) */}
                      {(isAdmin || isProveedor) && clientesFiltrados.length > 0 && (
                        <button
                          onClick={() => {
                            const todosLosFiltrados = clientesFiltrados;
                            const todosSeleccionados = todosLosFiltrados.every(c =>
                              currentWidget.clientesSeleccionados?.some(s => s.id === c.id)
                            );
                            if (todosSeleccionados) {
                              // Deseleccionar los filtrados (mantener los que no están en el filtro)
                              const idsAQuitar = new Set(todosLosFiltrados.map(c => c.id));
                              updateWidget(currentWidget.id, {
                                clientesSeleccionados: (currentWidget.clientesSeleccionados || []).filter(c => !idsAQuitar.has(c.id))
                              });
                            } else {
                              // Añadir todos los filtrados sin duplicar
                              const idsExistentes = new Set((currentWidget.clientesSeleccionados || []).map(c => c.id));
                              const nuevos = todosLosFiltrados.filter(c => !idsExistentes.has(c.id));
                              updateWidget(currentWidget.id, {
                                clientesSeleccionados: [...(currentWidget.clientesSeleccionados || []), ...nuevos]
                              });
                            }
                          }}
                          className="w-full mb-3 px-3 py-2 text-sm rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid"
                          style={
                            clientesFiltrados.length > 0 && clientesFiltrados.every(c =>
                              currentWidget.clientesSeleccionados?.some(s => s.id === c.id)
                            )
                              ? { borderColor: theme.primary, backgroundColor: theme.primary, color: '#ffffff' }
                              : { borderColor: theme.primary, color: theme.primary }
                          }
                        >
                          <CheckSquare size={16} className="inline mr-2" />
                          {clientesFiltrados.length > 0 && clientesFiltrados.every(c =>
                            currentWidget.clientesSeleccionados?.some(s => s.id === c.id)
                          )
                            ? `Deseleccionar Todos (${clientesFiltrados.length})`
                            : `Seleccionar Todos (${clientesFiltrados.length})`
                          }
                        </button>
                      )}

                      {/* Botón para seleccionar todas las clínicas */}
                      {isClient && esClinicaMatriz && clientesFiltrados.length > 0 && (
                        <button
                          onClick={() => {
                            const todosLosClientes = getClientesParaFiltrado();
                            if (currentWidget.clientesSeleccionados?.length === todosLosClientes.length) {
                              updateWidget(currentWidget.id, { clientesSeleccionados: [] });
                            } else {
                              updateWidget(currentWidget.id, { clientesSeleccionados: todosLosClientes });
                            }
                          }}
                          className="w-full mb-3 px-3 py-2 text-sm rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid"
                          style={
                            currentWidget.clientesSeleccionados?.length === getClientesParaFiltrado().length
                              ? { borderColor: theme.primary, backgroundColor: theme.primary, color: '#ffffff' }
                              : { borderColor: theme.primary, color: theme.primary }
                          }
                        >
                          <CheckSquare size={16} className="inline mr-2" />
                          {currentWidget.clientesSeleccionados?.length === getClientesParaFiltrado().length ? 
                            'Deseleccionar Todas' : 'Seleccionar Todas'}
                        </button>
                      )}
                      
                      {isClient && esClinicaMatriz && (
                        <div
                          className="text-xs text-center p-2 rounded"
                          style={{ color: theme.primary, backgroundColor: hexToRgba(theme.primary, 0.1) }}
                        >
                          {currentWidget.clientesSeleccionados?.length === 0 ? 
                            'Selecciona al menos una clínica para analizar' :
                            `Analizando ${currentWidget.clientesSeleccionados?.length} de ${clientesAsociados.length + 1} clínicas`
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Sección de Proveedores */}
              {(isAdmin || isClient) && (
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Users size={16} className="mr-2" />
                    {isClient ? 'Analizar por Proveedores' : 'Proveedores'}
                  </h3>
                  
                  <button
                    onClick={() => handleProveedorChange(null, 'selectAll')}
                    className="w-full mb-3 px-3 py-2 text-sm rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid"
                    style={
                      currentWidget.proveedoresSeleccionados.length === matrizProveedores.length
                        ? { borderColor: theme.primary, backgroundColor: theme.primary, color: '#ffffff' }
                        : { borderColor: theme.primary, color: theme.primary }
                    }
                    title={currentWidget.proveedoresSeleccionados.length === matrizProveedores.length ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                  >
                    <CheckSquare size={16} className="inline mr-2" />
                    {currentWidget.proveedoresSeleccionados.length === matrizProveedores.length ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                  </button>
                  
                  <div className="relative mb-3">
                    <input
                      type="text"
                      placeholder="Buscar proveedor..."
                      value={busquedaProveedor}
                      onChange={(e) => setBusquedaProveedor(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': theme.primary }}
                    />
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    {proveedoresFiltrados.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No se encontraron proveedores
                      </div>
                    ) : (
                      proveedoresFiltrados.map((proveedor, index) => (
                        <div
                          key={proveedor.id}
                          className="p-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors duration-200 hover:bg-gray-50"
                          style={
                            currentWidget.proveedoresSeleccionados.some(p => p.id === proveedor.id)
                              ? { backgroundColor: hexToRgba(theme.primary, 0.06) }
                              : {}
                          }
                          onClick={() => handleProveedorChange(proveedor, 'toggle')}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1 min-w-0">
                              <div 
                                className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                                style={{ backgroundColor: getProveedorColor(index) }}
                              />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">{proveedor.nombre}</h4>
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                  <span>{formatCurrency(proveedor.totalCompras)}</span>
                                  <span>{proveedor.numeroPedidos} pedidos</span>
                                </div>
                              </div>
                            </div>
                            
                            <input
                              type="checkbox"
                              checked={currentWidget.proveedoresSeleccionados.some(p => p.id === proveedor.id)}
                              onChange={() => toggleProveedorEnWidget(proveedor)}
                              className="ml-2"
                              style={{ accentColor: theme.primary }}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="bg-gray-100 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <Settings size={32} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Selecciona una Tarjeta</h3>
                <p className="text-gray-600 text-sm">
                  Haz click en cualquier tarjeta o crea una nueva para configurarla
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Componente para manejar el redimensionamiento de gráficas
const ResizeHandle = ({ onMouseDown, className = "" }) => (
  <div 
    className={`absolute bg-gray-400 hover:bg-gray-600 transition-colors duration-200 cursor-pointer ${className}`}
    onMouseDown={onMouseDown}
    style={{ borderRadius: '2px' }}
  />
);

// Componente de gráfica individual redimensionable y arrastrable
const ChartWidget = ({ 
  id, 
  title, 
  data, 
  chartType, 
  metricType,
  submetricaProductos,
  position, 
  size, 
  onMove, 
  onResize, 
  onRemove, 
  onDuplicate,
  onUpdateTitle,
  onExportWidget,
  formatCurrency,
  formatNumber,
  getProveedorColor,
  proveedoresSeleccionados,
  matrizProveedores,
  isSelected,
  onSelect,
  isLoading = false,
  clientesSeleccionados = [],
  tipoAnalisis = 'proveedor',
  tipoEje = 'proveedor',
  user,
  esClinicaMatriz = false
}) => {
  const { theme } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const [isExporting, setIsExporting] = useState(false);
  const widgetRef = useRef(null);
  const titleInputRef = useRef(null);

  const isAdmin = user?.permisos === 1;
  const isClient = user?.permisos === 5;
  const isProveedor = user?.permisos === 4;

  const handleExportWidget = async () => {
    setIsExporting(true);
    try {
      const success = await exportWidgetAsPNG(id, title);
      if (!success) {
        throw new Error('Error al exportar widget');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleTitleDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditingTitle(true);
    setTempTitle(title);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  };

  const handleTitleSubmit = () => {
    if (tempTitle.trim() && tempTitle !== title) {
      onUpdateTitle(id, tempTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      setTempTitle(title);
      setIsEditingTitle(false);
    }
  };

  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.resize-handle') || isEditingTitle) return;
    if (e.target.closest('.resize-handle')) return;
    
    setIsDragging(true);
    onSelect(id);
    const rect = widgetRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  }, [id, onSelect]);

  const handleResizeMouseDown = useCallback((e, direction) => {
    e.stopPropagation();
    setIsResizing(direction);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
    e.preventDefault();
  }, [size]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const container = widgetRef.current.parentElement;
        const containerRect = container.getBoundingClientRect();
        
        const newX = Math.max(0, Math.min(
          e.clientX - containerRect.left - dragOffset.x,
          containerRect.width - size.width
        ));
        const newY = Math.max(0, Math.min(
          e.clientY - containerRect.top - dragOffset.y,
          containerRect.height - size.height
        ));
        
        onMove(id, { x: newX, y: newY });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;
        
        if (isResizing.includes('right')) {
          newWidth = Math.max(300, resizeStart.width + deltaX);
        }
        if (isResizing.includes('left')) {
          newWidth = Math.max(300, resizeStart.width - deltaX);
        }
        if (isResizing.includes('bottom')) {
          newHeight = Math.max(200, resizeStart.height + deltaY);
        }
        if (isResizing.includes('top')) {
          newHeight = Math.max(200, resizeStart.height - deltaY);
        }
        
        onResize(id, { width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, isResizing, dragOffset, resizeStart, id, onMove, onResize, size]);

  const renderChart = () => {
    console.log(`🎨 Renderizando chart para widget ${id}:`, {
      isLoading,
      dataLength: data?.length || 0,
      hasData: data && data.length > 0
    });

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="relative mb-4">
              <div
                className="w-12 h-12 border-4 rounded-full animate-spin mx-auto"
                style={{
                  borderColor: hexToRgba(theme.primary, 0.25),
                  borderTopColor: theme.primary
                }}
              ></div>
              <TrendingUp className="w-6 h-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" style={{ color: theme.primary }} />
            </div>
            <p className="text-gray-600 font-medium">Cargando datos...</p>
            <p className="text-sm text-gray-500 mt-1">
              Actualizando gráfico para esta tarjeta
            </p>
          </div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      console.log(`❌ Widget ${id} sin datos - mostrando mensaje de configuración`);
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-gray-400 mb-2" style={{ paddingLeft: '100px' }}>
              <Filter size={48} />
            </div>
            <p className="text-gray-700 font-medium">Configura y aplica filtros</p>
            <p className="text-sm text-gray-500 mt-1">
              {isProveedor ? (
                <>
                  1. Tu empresa ya está incluida
                  <br />
                  2. Selecciona cliente(s) (opcional)
                  <br />
                  3. Cambia configuración automáticamente
                </>
              ) : isClient ? (
                <>
                  1. Tu empresa ya está incluida
                  <br />
                  2. Selecciona proveedores en el panel lateral
                  <br />
                  3. Cambia configuración automáticamente
                </>
              ) : (
                <>
                  1. Selecciona proveedores y/o clientes
                  <br />
                  2. Cambia configuración automáticamente
                </>
              )}
            </p>
          </div>
        </div>
      );
    }

    console.log(`✅ Widget ${id} con datos - renderizando gráfico`, data.length, 'elementos');

    const formatValue = (value) => {
      if (metricType === 'productos') {
        switch(submetricaProductos) {
          case 'facturacion_total': return formatCurrency(value);
          default: return formatNumber(value);
        }
      }
      return metricType === 'total' ? formatCurrency(value) : formatNumber(value);
    };

    const getDataKey = (item) => {
      if (metricType === 'productos') {
        switch(submetricaProductos) {
          case 'numero_pedidos': return item.numeroPedidos;
          case 'facturacion_total': return item.facturacionTotal;
          case 'clientes_distintos': return item.clientesDistintos;
          default: return item.totalVendido;
        }
      }
      return item.value || item[Object.keys(item).find(key => key !== 'mes' && key !== 'nombre')] || 0;
    };

    switch(chartType) {
      case 'bar':
        if (metricType === 'productos') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="nombre" 
                  stroke="#666" 
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#666" fontSize={12} tickFormatter={formatValue} />
                {/* Sin labelFormatter: Recharts lo ignora al usar `content` propio.
                    El nombre completo lo resuelve CustomTooltip desde el dato. */}
                <Tooltip
                  content={<CustomTooltip metricType={metricType} submetricaProductos={submetricaProductos} formatCurrency={formatCurrency} formatNumber={formatNumber} />}
                />
                {/* Una fila por producto: siempre una sola serie, coloreada por producto */}
                <Bar
                  dataKey={(item) => getDataKey(item)}
                  fill={theme.primary}
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          );
        } else {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey={data[0] && data[0].mes ? "mes" : "nombre"} 
                  stroke="#666" 
                  fontSize={12}
                  angle={data[0] && data[0].mes ? 0 : -45}
                  textAnchor={data[0] && data[0].mes ? "middle" : "end"}
                  height={data[0] && data[0].mes ? 30 : 80}
                />
                <YAxis stroke="#666" fontSize={12} tickFormatter={formatValue} />
                <Tooltip content={<CustomTooltip metricType={metricType} submetricaProductos={submetricaProductos} formatCurrency={formatCurrency} formatNumber={formatNumber} />} />
                {renderDataSeries('Bar')}
              </BarChart>
            </ResponsiveContainer>
          );
        }

      case 'line':
        if (metricType === 'productos') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="nombre" 
                  stroke="#666" 
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#666" fontSize={12} tickFormatter={formatValue} />
                {/* Sin labelFormatter: Recharts lo ignora al usar `content` propio.
                    El nombre completo lo resuelve CustomTooltip desde el dato. */}
                <Tooltip
                  content={<CustomTooltip metricType={metricType} submetricaProductos={submetricaProductos} formatCurrency={formatCurrency} formatNumber={formatNumber} />}
                />
                <Line 
                  type="monotone" 
                  dataKey={(item) => getDataKey(item)}
                  stroke={theme.primary}
                  strokeWidth={3}
                  dot={{ fill: theme.primary, strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          );
        } else {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey={data[0] && data[0].mes ? "mes" : "nombre"} 
                  stroke="#666" 
                  fontSize={12}
                  angle={data[0] && data[0].mes ? 0 : -45}
                  textAnchor={data[0] && data[0].mes ? "middle" : "end"}
                  height={data[0] && data[0].mes ? 30 : 80}
                />
                <YAxis stroke="#666" fontSize={12} tickFormatter={formatValue} />
                <Tooltip content={<CustomTooltip metricType={metricType} submetricaProductos={submetricaProductos} formatCurrency={formatCurrency} formatNumber={formatNumber} />} />
                {renderDataSeries('Line')}
              </LineChart>
            </ResponsiveContainer>
          );
        }

      case 'pie':
        const pieData = data.slice(0, 8).map((item, index) => ({
          ...item,
          value: metricType === 'productos' ? getDataKey(item) : (item.value || item[Object.keys(item).find(key => key !== 'mes' && key !== 'nombre')] || 0),
          fill: getProveedorColor(index)
        }));
        
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={Math.min(size.width, size.height) * 0.15}
                dataKey="value"
                label={({name, value}) => `${name}: ${formatValue(value)}`}
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip 
                content={<CustomTooltip metricType={metricType} submetricaProductos={submetricaProductos} formatCurrency={formatCurrency} formatNumber={formatNumber} />}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mes" stroke="#666" fontSize={12} />
              <YAxis stroke="#666" fontSize={12} tickFormatter={formatValue} />
              <Tooltip content={<CustomTooltip metricType={metricType} submetricaProductos={submetricaProductos} formatCurrency={formatCurrency} formatNumber={formatNumber} />} />
              {renderDataSeries('Area')}
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return renderChart.bind(this)();
    }

    function renderDataSeries(chartComponent) {
      if (!proveedoresSeleccionados) return null;

      const tipoEjeActual = tipoEje || 'proveedor';
      
      const isAdmin = user?.permisos === 1;
      const isClient = user?.permisos === 5;
      const isProveedor = user?.permisos === 4;
      
      if (isProveedor) {
        if (tipoEjeActual === 'cliente' && clientesSeleccionados?.length > 1) {
          return clientesSeleccionados.map((cliente, index) => {
            const Component = chartComponent === 'Bar' ? Bar : chartComponent === 'Line' ? Line : Area;
            return (
              <Component
                key={cliente.id}
                type={chartComponent === 'Line' ? "monotone" : undefined}
                dataKey={cliente.razon_social}
                fill={getProveedorColor(index)}
                stroke={getProveedorColor(index)}
                stackId={chartComponent === 'Area' ? "1" : undefined}
                fillOpacity={chartComponent === 'Area' ? 0.3 : undefined}
                strokeWidth={chartComponent === 'Line' ? 3 : undefined}
                dot={chartComponent === 'Line' ? { fill: getProveedorColor(index), strokeWidth: 2, r: 4 } : undefined}
                radius={chartComponent === 'Bar' ? [2, 2, 0, 0] : undefined}
              />
            );
          });
        } else {
          const Component = chartComponent === 'Bar' ? Bar : chartComponent === 'Line' ? Line : Area;
          return (
            <Component
              type={chartComponent === 'Line' ? "monotone" : undefined}
              dataKey={user?.proveedor_info?.nombre || 'Tu empresa'}
              fill={theme.primary}
              stroke={theme.primary}
              stackId={chartComponent === 'Area' ? "1" : undefined}
              fillOpacity={chartComponent === 'Area' ? 0.3 : undefined}
              strokeWidth={chartComponent === 'Line' ? 3 : undefined}
              dot={chartComponent === 'Line' ? { fill: theme.primary, strokeWidth: 2, r: 4 } : undefined}
              radius={chartComponent === 'Bar' ? [2, 2, 0, 0] : undefined}
            />
          );
        }
      }
      
      if (isClient && !esClinicaMatriz) {
        return proveedoresSeleccionados.map((proveedor, index) => {
          const Component = chartComponent === 'Bar' ? Bar : chartComponent === 'Line' ? Line : Area;
          return (
            <Component
              key={proveedor.id}
              type={chartComponent === 'Line' ? "monotone" : undefined}
              dataKey={proveedor.nombre}
              fill={getProveedorColor(matrizProveedores.findIndex(p => p.id === proveedor.id))}
              stroke={getProveedorColor(matrizProveedores.findIndex(p => p.id === proveedor.id))}
              stackId={chartComponent === 'Area' ? "1" : undefined}
              fillOpacity={chartComponent === 'Area' ? 0.3 : undefined}
              strokeWidth={chartComponent === 'Line' ? 3 : undefined}
              dot={chartComponent === 'Line' ? { fill: getProveedorColor(matrizProveedores.findIndex(p => p.id === proveedor.id)), strokeWidth: 2, r: 4 } : undefined}
              radius={chartComponent === 'Bar' ? [2, 2, 0, 0] : undefined}
            />
          );
        });
      }
      
      if (isAdmin || (isClient && esClinicaMatriz)) {
        if (tipoEjeActual === 'cliente' && proveedoresSeleccionados?.length === 1 && clientesSeleccionados?.length > 1) {
          return clientesSeleccionados.map((cliente, index) => {
            const Component = chartComponent === 'Bar' ? Bar : chartComponent === 'Line' ? Line : Area;
            return (
              <Component
                key={cliente.id}
                type={chartComponent === 'Line' ? "monotone" : undefined}
                dataKey={cliente.razon_social}
                fill={getProveedorColor(index)}
                stroke={getProveedorColor(index)}
                stackId={chartComponent === 'Area' ? "1" : undefined}
                fillOpacity={chartComponent === 'Area' ? 0.3 : undefined}
                strokeWidth={chartComponent === 'Line' ? 3 : undefined}
                dot={chartComponent === 'Line' ? { fill: getProveedorColor(index), strokeWidth: 2, r: 4 } : undefined}
                radius={chartComponent === 'Bar' ? [2, 2, 0, 0] : undefined}
              />
            );
          });
        } else {
          return proveedoresSeleccionados.map((proveedor, index) => {
            const Component = chartComponent === 'Bar' ? Bar : chartComponent === 'Line' ? Line : Area;
            return (
              <Component
                key={proveedor.id}
                type={chartComponent === 'Line' ? "monotone" : undefined}
                dataKey={proveedor.nombre}
                fill={getProveedorColor(matrizProveedores.findIndex(p => p.id === proveedor.id))}
                stroke={getProveedorColor(matrizProveedores.findIndex(p => p.id === proveedor.id))}
                stackId={chartComponent === 'Area' ? "1" : undefined}
                fillOpacity={chartComponent === 'Area' ? 0.3 : undefined}
                strokeWidth={chartComponent === 'Line' ? 3 : undefined}
                dot={chartComponent === 'Line' ? { fill: getProveedorColor(matrizProveedores.findIndex(p => p.id === proveedor.id)), strokeWidth: 2, r: 4 } : undefined}
                radius={chartComponent === 'Bar' ? [2, 2, 0, 0] : undefined}
              />
            );
          });
        }
      }
    }
  };

  return (
    <div
      ref={widgetRef}
      data-widget-id={id}
      className={`absolute bg-white rounded-xl shadow-lg border-2 overflow-hidden ${
        isSelected ? 'shadow-xl' : 'border-gray-200'
      } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: isSelected ? 1000 : 1,
        borderColor: isSelected ? theme.primary : undefined
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <Move size={16} className="text-gray-400 mr-2" />
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onBlur={handleTitleSubmit}
              onKeyDown={handleTitleKeyDown}
              className="text-sm font-semibold text-gray-800 bg-white border rounded px-2 py-1 focus:outline-none focus:ring-2"
              style={{ minWidth: '150px', maxWidth: '250px', borderColor: theme.primary }}
            />
          ) : (
            <h3 
              className="text-sm font-semibold text-gray-800 truncate cursor-pointer transition-colors duration-200"
              onDoubleClick={handleTitleDoubleClick}
              title="Doble click para editar"
              onMouseEnter={e => e.currentTarget.style.color = theme.primary}
              onMouseLeave={e => e.currentTarget.style.color = ''}
            >
              {title}
            </h3>
          )}
          <div className="ml-2 text-xs text-gray-500">
            ({isProveedor ? 'Tu empresa' : `${proveedoresSeleccionados.length} prov.`}
            {clientesSeleccionados.length > 0 && (
              <span>
                {' • '}
                {clientesSeleccionados.length === 1 
                  ? clientesSeleccionados[0].razon_social.substring(0, 10) + '...'
                  : `${clientesSeleccionados.length} clientes`
                }
              </span>
            )})
          </div>
          {isLoading && (
            <div className="ml-2">
              <Loader size={12} className="animate-spin" style={{ color: theme.primary }} />
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleExportWidget();
            }}
            disabled={isExporting}
            className="p-1 rounded transition-colors duration-200 disabled:opacity-50 hover:bg-gray-100"
            title="Exportar esta tarjeta como PNG"
          >
            {isExporting ? (
              <Loader size={14} className="animate-spin" style={{ color: theme.primary }} />
            ) : (
              <Image size={14} style={{ color: theme.primary }} />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(id);
            }}
            className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
            title="Duplicar"
          >
            <Copy size={14} className="text-gray-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(id);
            }}
            className="p-1 hover:bg-red-100 rounded transition-colors duration-200"
            title="Eliminar"
          >
            <X size={14} className="text-red-600" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4" style={{ height: 'calc(100% - 50px)' }}>
        {renderChart()}
      </div>

      {/* Resize Handles */}
      <ResizeHandle
        className="resize-handle bottom-0 right-0 w-4 h-4 cursor-se-resize"
        onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
      />
      <ResizeHandle
        className="resize-handle bottom-0 left-1/2 w-2 h-1 cursor-s-resize transform -translate-x-1/2"
        onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
      />
      <ResizeHandle
        className="resize-handle right-0 top-1/2 w-1 h-2 cursor-e-resize transform -translate-y-1/2"
        onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
      />
    </div>
  );
};

export default function ModularProveedorAnalytics() {
  const { theme } = useTheme();
  const { user: contextUser } = useContext(AuthContext) || {};
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isUpdatingWidget, setIsUpdatingWidget] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // Estados para configuraciones guardadas
  const [configuracionesGuardadas, setConfiguracionesGuardadas] = useState([]);
  const [configuracionSeleccionada, setConfiguracionSeleccionada] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  
  const user = userInfo || contextUser || null;
  
  const isAdmin = user?.permisos === 1;
  const isClient = user?.permisos === 5;
  const isProveedor = user?.permisos === 4;
  
  // Estados para datos
  const [kpisGenerales, setKpisGenerales] = useState(null);
  const [matrizProveedores, setMatrizProveedores] = useState([]);
  const [listaClientes, setListaClientes] = useState([]);
  const [datosTemporales, setDatosTemporales] = useState({});
  const [datosTemporalesClientes, setDatosTemporalesClientes] = useState({});
  const [topProductos, setTopProductos] = useState({});
  const [productosComparativa, setProductosComparativa] = useState({});

  // Estados para clínicas matriz
  const [esClinicaMatriz, setEsClinicaMatriz] = useState(false);
  const [clientesAsociados, setClientesAsociados] = useState([]);
  const [clientesMatrizSeleccionados, setClientesMatrizSeleccionados] = useState([]);
  const [isLoadingClientesMatriz, setIsLoadingClientesMatriz] = useState(false);
  
  // Estados para filtros GLOBALES
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  
  // Estados para búsquedas
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  
  // Estados para el panel lateral
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  
  // Estados para widgets de gráficas
  const [widgets, setWidgets] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [nextWidgetId, setNextWidgetId] = useState(1);
  
  const canvasRef = useRef(null);
  
  // Colores para proveedores - el primero es el color del tema
  const coloresProveedores = [
    theme.primary,
    '#4f46e5', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280',
    '#14b8a6', '#3b82f6', '#eab308', '#dc2626', '#a855f7'
  ];

  const fetchListaClientes = useCallback(async () => {
    const cacheKey = `lista_clientes_${user?.permisos}_${user?.clinica_matriz}_${esClinicaMatriz ? 'matriz' : 'normal'}_${estadoFiltro}_${fechaDesde}_${fechaHasta}`;
    
    if (!esClinicaMatriz) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        setListaClientes(cached);
        return cached;
      }
    }

    try {
      const token = localStorage.getItem('token');
      
      const endpoint = isProveedor ?
        `${API_URL}/api/analisis/clientes-lista-proveedor` :
        `${API_URL}/api/analisis/clientes-lista`;

      const params = new URLSearchParams();
      if (estadoFiltro) {
        params.append('estado', estadoFiltro);
        console.log('📋 Enviando filtro estado:', estadoFiltro);
      }
      if (fechaDesde) {
        params.append('fecha_desde', fechaDesde);
        console.log('📋 Enviando filtro fecha_desde:', fechaDesde);
      }
      if (fechaHasta) {
        params.append('fecha_hasta', fechaHasta);
        console.log('📋 Enviando filtro fecha_hasta:', fechaHasta);
      }

      const finalEndpoint = params.toString() ? `${endpoint}?${params}` : endpoint;
      console.log('🔍 Llamando endpoint clientes con filtros:', finalEndpoint);

      const response = await fetch(finalEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al obtener lista de clientes');
      const data = await response.json();
      
      console.log('📋 Lista de clientes cargada:', data.clientes?.length || 0);
      
      if (isClient && esClinicaMatriz) {
        console.log('🏢 DATOS CLÍNICA MATRIZ RECIBIDOS:');
        data.clientes?.forEach(cliente => {
          console.log(`   - ${cliente.razon_social}: ${cliente.pedidos_activos} pedidos, €${cliente.total_facturado}`);
        });
      }
      
      if (!esClinicaMatriz) {
        setCachedData(cacheKey, data.clientes || []);
      }
      
      setListaClientes(data.clientes || []);
      return data.clientes || [];
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      return [];
    }
  }, [isProveedor, user?.permisos, user?.clinica_matriz, esClinicaMatriz, estadoFiltro, fechaDesde, fechaHasta, isClient]);
 

  useEffect(() => {
    if (isClient && esClinicaMatriz && !isInitialLoading && !isApplyingFilters) {
      console.log('Detectado cambio en filtros para clínica matriz, recargando datos...');
      
      const recargarDatosConFiltros = async () => {
        try {
          await fetchListaClientes();
          console.log('Mis Clínicas recargado con filtros aplicados');
        } catch (err) {
          console.error('Error al recargar datos con filtros:', err);
        }
      };
      
      const timer = setTimeout(() => {
        recargarDatosConFiltros();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [estadoFiltro, fechaDesde, fechaHasta, isClient, esClinicaMatriz, isInitialLoading, isApplyingFilters, fetchListaClientes]);

  const fetchKpisGenerales = useCallback(async () => {
    const cacheKey = `kpis_${estadoFiltro}_${fechaDesde}_${fechaHasta}_${user?.permisos}_${esClinicaMatriz ? clientesMatrizSeleccionados.map(c => c.id).join('-') : ''}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setKpisGenerales(cached);
      return cached;
    }

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (estadoFiltro) params.append('estado', estadoFiltro);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);

      if (isClient && esClinicaMatriz && clientesMatrizSeleccionados.length > 0) {
        const clientesIds = clientesMatrizSeleccionados.map(c => c.id).join(',');
        params.append('clientes_ids', clientesIds);
      }

      let endpoint;
      if (isProveedor) {
        endpoint = `${API_URL}/api/analisis/proveedor-kpis?${params}`;
      } else if (isClient) {
        endpoint = `${API_URL}/api/analisis/cliente-kpis?${params}`;
      } else {
        endpoint = `${API_URL}/api/analisis/kpis-generales?${params}`;
      }

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al obtener KPIs generales');
      const data = await response.json();
      
      setCachedData(cacheKey, data);
      setKpisGenerales(data);
      return data;
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      return null;
    }
  }, [estadoFiltro, fechaDesde, fechaHasta, isProveedor, isClient, user?.permisos, esClinicaMatriz, clientesMatrizSeleccionados]);
  
  const fetchMatrizProveedores = useCallback(async () => {
    if (isProveedor) {
      if (userInfo?.proveedor_info) {
        const proveedorPropio = {
          id: userInfo.proveedor_info.id,
          nombre: userInfo.proveedor_info.nombre,
          totalCompras: 0,
          numeroPedidos: 0,
          cantidadArticulos: 0,
          promedioMensual: 0,
          porcentajeTotal: 100
        };
        setMatrizProveedores([proveedorPropio]);
        return [proveedorPropio];
      }
      return [];
    }

    const cacheKey = `matriz_proveedores_${estadoFiltro}_${fechaDesde}_${fechaHasta}_${user?.permisos}_${esClinicaMatriz ? clientesMatrizSeleccionados.map(c => c.id).join('-') : ''}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setMatrizProveedores(cached);
      return cached;
    }

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (estadoFiltro) params.append('estado', estadoFiltro);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);

      if (isClient && esClinicaMatriz && clientesMatrizSeleccionados.length > 0) {
        const clientesIds = clientesMatrizSeleccionados.map(c => c.id).join(',');
        params.append('clientes_ids', clientesIds);
        console.log('🏢 Enviando clientes_ids para matriz:', clientesIds);
      }

      const endpoint = isClient ? 
        `${API_URL}/api/analisis/matriz-proveedores-cliente?${params}` :
        `${API_URL}/api/analisis/matriz-proveedores?${params}`;

      console.log('🔍 Llamando endpoint:', endpoint);

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al obtener matriz de proveedores');
      const data = await response.json();
      
      console.log('✅ Proveedores obtenidos:', data.matrizProveedores?.length || 0);
      
      setCachedData(cacheKey, data.matrizProveedores || []);
      setMatrizProveedores(data.matrizProveedores || []);
      return data.matrizProveedores || [];
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      return [];
    }
  }, [estadoFiltro, fechaDesde, fechaHasta, isProveedor, isClient, userInfo?.proveedor_info, user?.permisos, esClinicaMatriz, clientesMatrizSeleccionados]);
  

  useEffect(() => {
    if (isClient && esClinicaMatriz && clientesMatrizSeleccionados.length > 0 && !isInitialLoading) {
      console.log('Clientes matriz seleccionados cambiaron, recargando datos...');
      
      const recargarDatos = async () => {
        try {
          await fetchKpisGenerales();
          await sleep(200);
          await fetchMatrizProveedores();
          await sleep(200);
          await fetchListaClientes();
          console.log('Datos recargados después de cambio en clientes matriz');
        } catch (err) {
          console.error('Error al recargar datos:', err);
        }
      };
      
      recargarDatos();
    }
  }, [clientesMatrizSeleccionados, isClient, esClinicaMatriz, isInitialLoading, fetchKpisGenerales, fetchMatrizProveedores, fetchListaClientes]);


  const getProveedorColor = (index) => {
    if (typeof index !== 'number' || index < 0) {
      return coloresProveedores[0];
    }
    return coloresProveedores[index % coloresProveedores.length];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-ES').format(num);
  };

  // ===== FUNCIONES PARA CONFIGURACIONES =====
  
  const cargarConfiguraciones = async () => {
    try {
      setIsLoadingConfigs(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/dashboard/configuraciones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar configuraciones');
      const data = await response.json();
      setConfiguracionesGuardadas(data.configuraciones || []);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar configuraciones guardadas');
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  const cargarConfiguracion = async (configId) => {
    try {
      setIsApplyingFilters(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/dashboard/configuraciones/${configId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar configuración');
      const data = await response.json();
      const config = JSON.parse(data.configuracion.configuracion);

      setWidgets(config.widgets || []);
      setNextWidgetId(config.nextWidgetId || 1);
      setSelectedWidget(config.selectedWidget || null);
      setEstadoFiltro(config.estadoFiltro || '');
      setFechaDesde(config.fechaDesde || '');
      setFechaHasta(config.fechaHasta || '');
      setSidePanelOpen(config.sidePanelOpen !== undefined ? config.sidePanelOpen : true);

      setBusquedaProveedor('');
      setBusquedaCliente('');
      setBusquedaProducto('');

      console.log('✅ Configuración cargada:', data.configuracion.nombre);
      console.log('📊 Widgets cargados:', config.widgets?.length || 0);
      
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar la configuración: ' + err.message);
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const guardarConfiguracion = async (nombre) => {
    try {
      setIsSavingConfig(true);
      const token = localStorage.getItem('token');
      
      const configuracion = {
        widgets,
        nextWidgetId,
        selectedWidget,
        estadoFiltro,
        fechaDesde,
        fechaHasta,
        sidePanelOpen,
        fechaGuardado: new Date().toISOString()
      };

      const response = await fetch(`${API_URL}/api/dashboard/configuraciones`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombre,
          configuracion
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar configuración');
      }

      await cargarConfiguraciones();
      setShowSaveModal(false);
      
      console.log('✅ Configuración guardada:', nombre);
      
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleConfiguracionChange = (e) => {
    const configId = e.target.value;
    setConfiguracionSeleccionada(configId);
    
    if (configId) {
      cargarConfiguracion(configId);
    }
  };

  // ===== FUNCIONES API OPTIMIZADAS =====
  
  const fetchUserInfo = async () => {
    const cacheKey = 'user_info';
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      
      const response = await fetch(`${API_URL}/api/user/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Sesión expirada. Inicia sesión nuevamente.');
        }
        throw new Error('Error al obtener información del usuario');
      }
      
      const data = await response.json();
      
      setCachedData(cacheKey, data);
      setUserInfo(data);
      
      if (contextUser && data) {
        Object.assign(contextUser, data);
      }
      
      return data;
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      throw err;
    }
  };

  const autoSelectProveedorForProveedorUser = useCallback(() => {
    if (isProveedor && userInfo?.proveedor_info) {
      const proveedorPropio = {
        id: userInfo.proveedor_info.id,
        nombre: userInfo.proveedor_info.nombre,
        totalCompras: 0,
        numeroPedidos: 0,
        cantidadArticulos: 0,
        promedioMensual: 0,
        porcentajeTotal: 0
      };
      
      setWidgets(prev => prev.map(widget => ({
        ...widget,
        proveedoresSeleccionados: [proveedorPropio]
      })));
    }
  }, [isProveedor, userInfo?.proveedor_info]);

  const autoSelectClienteForClientUser = useCallback(() => {
    if (isClient && !esClinicaMatriz && userInfo?.clinica_info) {
      const clientePropio = {
        id: userInfo.clinica_info.id,
        razon_social: userInfo.clinica_info.razon_social,
        total_facturado: 0,
        pedidos_activos: 0
      };
      
      setWidgets(prev => prev.map(widget => ({
        ...widget,
        clientesSeleccionados: [clientePropio]
      })));
    }
  }, [isClient, esClinicaMatriz, userInfo?.clinica_info]);

  const autoSelectAllClinicasForMatriz = useCallback(() => {
    if (isClient && esClinicaMatriz && clientesAsociados.length > 0 && clientesMatrizSeleccionados.length === 0 && userInfo?.clinica_info) {
      const todosLosClientes = [
        { id: userInfo.clinica_info.id, razon_social: userInfo.clinica_info.razon_social + ' (Matriz)', total_facturado: 0, pedidos_activos: 0 },
        ...clientesAsociados.map(c => ({ ...c, total_facturado: 0, pedidos_activos: 0 }))
      ];
      setClientesMatrizSeleccionados(todosLosClientes);
    }
  }, [isClient, esClinicaMatriz, clientesAsociados, clientesMatrizSeleccionados.length, userInfo?.clinica_info]);

  // Función para crear widgets iniciales
  useEffect(() => {
    if (widgets.length === 0) {
      const initialWidgets = [
        {
          id: 1,
          title: 'Tarjeta 1',
          chartType: 'line',
          metricType: 'total',
          submetricaProductos: 'total_vendido',
          position: { x: 50, y: 30 },
          size: { width: 580, height: 300 },
          data: [],
          proveedoresSeleccionados: [],
          clientesSeleccionados: [],
          tipoAnalisis: 'total',
          tipoEje: 'proveedor',
          isLoading: false
        },
        {
          id: 3,
          title: 'Tarjeta 3',
          chartType: 'bar',
          metricType: 'pedidos',
          submetricaProductos: 'numero_pedidos',
          position: { x: 650, y: 30 },
          size: { width: 600, height: 620 },
          data: [],
          proveedoresSeleccionados: [],
          clientesSeleccionados: [],
          tipoAnalisis: 'total',
          tipoEje: 'proveedor',
          isLoading: false
        },
        {
          id: 2,
          title: 'Tarjeta 2',
          chartType: 'pie',
          metricType: 'articulos',
          submetricaProductos: 'total_vendido',
          position: { x: 50, y: 350 },
          size: { width: 580, height: 300 },
          data: [],
          proveedoresSeleccionados: [],
          clientesSeleccionados: [],
          tipoAnalisis: 'total',
          tipoEje: 'proveedor',
          isLoading: false
        },
        {
          id: 4,
          title: 'Tarjeta 4',
          chartType: 'bar',
          metricType: 'pedidos',
          submetricaProductos: 'numero_pedidos',
          position: { x: 1270, y: 30 },
          size: { width: 580, height: 620 },
          data: [],
          proveedoresSeleccionados: [],
          clientesSeleccionados: [],
          tipoAnalisis: 'total',
          tipoEje: 'proveedor',
          isLoading: false
        },
      ];
      setWidgets(initialWidgets);
      setNextWidgetId(5);
      setSelectedWidget(1);
    }
  }, []);

  useEffect(() => {
    if (userInfo && widgets.length > 0) {
      if (isProveedor) {
        autoSelectProveedorForProveedorUser();
      } else if (isClient) {
        autoSelectClienteForClientUser();
      }
    }
  }, [userInfo, widgets.length, isProveedor, isClient, autoSelectProveedorForProveedorUser, autoSelectClienteForClientUser]);

  const fetchClientesMatrizAsociados = useCallback(async () => {
    if (!isClient || !userInfo?.clinica_info) return;
    
    try {
      setIsLoadingClientesMatriz(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('No hay token disponible para clientes matriz');
        setEsClinicaMatriz(false);
        setClientesAsociados([]);
        return { esMatriz: false, clientesAsociados: [] };
      }
      
      const response = await fetch(`${API_URL}/api/analisis/clientes-matriz-asociados`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Token inválido o expirado para clientes matriz');
          throw new Error('Token no válido');
        }
        throw new Error('Error al obtener clientes asociados');
      }
      
      const data = await response.json();
      
      setEsClinicaMatriz(data.esMatriz);
      setClientesAsociados(data.clientesAsociados || []);
      
      if (data.esMatriz && data.clientesAsociados?.length > 0 && clientesMatrizSeleccionados.length === 0) {
        const todosLosClientes = [data.clienteMatriz, ...data.clientesAsociados];
        setClientesMatrizSeleccionados(todosLosClientes);
      }
      
      return data;
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Token no válido') {
        setError(err.message);
      }
      return { esMatriz: false, clientesAsociados: [] };
    } finally {
      setIsLoadingClientesMatriz(false);
    }
  }, [isClient, userInfo?.clinica_info, clientesMatrizSeleccionados.length]);

  const fetchDatosTemporalesComparativa = async (widgetId, proveedorIds, clienteIds = []) => {
    if (proveedorIds.length === 0) {
      setDatosTemporales(prev => ({ ...prev, [widgetId]: {} }));
      return;
    }

    setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: true } : w));

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (estadoFiltro) params.append('estado', estadoFiltro);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      
      if (isClient && esClinicaMatriz) {
        if (clienteIds.length > 0) {
          params.append('clientes_ids', clienteIds.join(','));
          console.log('🔧 Clínica matriz usando clienteIds:', clienteIds);
        } else if (clientesMatrizSeleccionados.length > 0) {
          const clientesIds = clientesMatrizSeleccionados.map(c => c.id).join(',');
          params.append('clientes_ids', clientesIds);
          console.log('🔧 Clínica matriz usando clientesMatrizSeleccionados:', clientesIds);
        }
      } else if (clienteIds.length > 0) {
        params.append('clientes_ids', clienteIds.join(','));
      }

      if (isProveedor && userInfo?.proveedor_info) {
        // código de proveedor existente
      } else {
        const datosIndexados = {};
        
        console.log(`🔄 Iniciando fetch para ${proveedorIds.length} proveedores:`, proveedorIds);
        
        for (const proveedorId of proveedorIds) {
          try {
            console.log(`🔄 Fetch proveedor ${proveedorId}...`);
            console.log(`🔄 URL: ${API_URL}/api/analisis/proveedor-temporal/${proveedorId}?${params}`);
            
            const response = await fetch(`${API_URL}/api/analisis/proveedor-temporal/${proveedorId}?${params}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
              const data = await response.json();
              
              console.log(`🔍 Respuesta completa proveedor ${proveedorId}:`, data);
              console.log(`🔍 datosTemporales:`, data.datosTemporales);
              console.log(`🔍 Cantidad de elementos:`, data.datosTemporales?.length || 0);
              
              if (data.datosTemporales && data.datosTemporales.length > 0) {
                console.log(`🔍 Primer elemento:`, data.datosTemporales[0]);
              }
              
              datosIndexados[proveedorId] = data.datosTemporales || [];
              console.log(`✅ Proveedor ${proveedorId}: ${data.datosTemporales?.length || 0} meses obtenidos`);
            } else {
              console.log(`❌ Error HTTP ${response.status} para proveedor ${proveedorId}`);
              const errorText = await response.text();
              console.log(`❌ Respuesta de error:`, errorText);
              datosIndexados[proveedorId] = [];
            }
            
            await sleep(200);
          } catch (err) {
            console.error(`❌ Error con proveedor ${proveedorId}:`, err);
            datosIndexados[proveedorId] = [];
          }
        }
        
        console.log('💾 Guardando datos en datosTemporales para widget', widgetId, ':', Object.keys(datosIndexados));
        console.log('💾 Total elementos por proveedor:', Object.entries(datosIndexados).map(([id, data]) => `${id}: ${data.length} meses`));
        
        setDatosTemporales(prev => {
          const newState = { ...prev, [widgetId]: datosIndexados };
          console.log('💾 Estado datosTemporales actualizado:', Object.keys(newState));
          return newState;
        });
      }
    } catch (err) {
      console.error('❌ Error en fetchDatosTemporalesComparativa:', err);
      setError(err.message);
    } finally {
      setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: false } : w));
    }
  };

  const fetchDatosTemporalesClientes = async (widgetId, clienteIds, proveedorIds = []) => {
    if (clienteIds.length === 0) {
      setDatosTemporalesClientes(prev => ({ ...prev, [widgetId]: {} }));
      return;
    }

    setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: true } : w));

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (estadoFiltro) params.append('estado', estadoFiltro);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      
      if (isProveedor && userInfo?.proveedor_info) {
        params.append('proveedores_ids', userInfo.proveedor_info.id);
      } else if (proveedorIds.length > 0) {
        params.append('proveedores_ids', proveedorIds.join(','));
      }

      const datosIndexados = {};
      
      console.log('🔧 fetchDatosTemporalesClientes para clínicas:', { 
        isClient, 
        esClinicaMatriz, 
        clienteIds: clienteIds.length,
        clientesMatrizSeleccionados: clientesMatrizSeleccionados.length 
      });
      
      const clientesParaAnalizar = clienteIds;
      
      for (const clienteId of clientesParaAnalizar) {
        try {
          const endpoint = proveedorIds.length > 0 || (isProveedor && userInfo?.proveedor_info) ?
            `${API_URL}/api/analisis/cliente-temporal-proveedores/${clienteId}` :
            `${API_URL}/api/analisis/cliente-temporal/${clienteId}`;

          const response = await fetch(`${endpoint}?${params}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            const data = await response.json();
            datosIndexados[clienteId] = data.datosTemporales || [];
            console.log(`✅ Datos obtenidos para cliente ${clienteId}:`, data.datosTemporales?.length || 0, 'meses');
          }
          
          await sleep(200);
        } catch (err) {
          console.error(`Error con cliente ${clienteId}:`, err);
          datosIndexados[clienteId] = [];
        }
      }
      
      console.log('🔧 Resultado final datosTemporalesClientes:', Object.keys(datosIndexados).length, 'clientes');
      setDatosTemporalesClientes(prev => ({ ...prev, [widgetId]: datosIndexados }));
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: false } : w));
    }
  };

  // Petición cruda a /analisis/top-productos. Devuelve el array de productos y
  // NO toca el estado, para que la use tanto el widget (con su TOP 20) como la
  // exportación a Excel (que necesita el listado completo).
  //
  // `limite`: undefined -> el backend aplica su TOP 20 por defecto.
  //           0         -> sin límite.
  // `rango` ({desde, hasta}) pisa el filtro de fechas de la pantalla. Lo usan
  // los troceados por meses para sortear el tope de un backend antiguo.
  const pedirTopProductos = async (proveedorIds = [], clienteIds = [], limite, rango) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (estadoFiltro) params.append('estado', estadoFiltro);
    const desde = rango?.desde ?? fechaDesde;
    const hasta = rango?.hasta ?? fechaHasta;
    if (desde) params.append('fecha_desde', desde);
    if (hasta) params.append('fecha_hasta', hasta);

    if (isClient && esClinicaMatriz) {
      if (clienteIds.length > 0) {
        params.append('clientes_ids', clienteIds.join(','));
        console.log('🔧 Top productos clínica matriz usando clienteIds:', clienteIds);
      } else if (clientesMatrizSeleccionados.length > 0) {
        const clientesIds = clientesMatrizSeleccionados.map(c => c.id).join(',');
        params.append('clientes_ids', clientesIds);
        console.log('🔧 Top productos clínica matriz usando clientesMatrizSeleccionados:', clientesIds);
      }
    } else if (clienteIds.length > 0) {
      params.append('clientes_ids', clienteIds.join(','));
    }

    const finalProveedorIds = isProveedor && userInfo?.proveedor_info
      ? [userInfo.proveedor_info.id]
      : (proveedorIds || []);
    if (finalProveedorIds.length > 0) {
      params.append('proveedores', finalProveedorIds.join(','));
    }

    if (limite !== undefined) params.append('limite', String(limite));

    const response = await fetch(`${API_URL}/api/analisis/top-productos?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Error al obtener TOP productos');
    const data = await response.json();
    return data.productos || [];
  };

  // Cuántas filas devuelve el backend cuando aplica su tope por defecto. Sirve
  // para detectar un backend que NO entiende `limite` (ver pedirProductosPorClinica).
  const TOPE_POR_DEFECTO_BACKEND = 20;

  // Clínicas efectivas de un widget, con la misma regla que aplica
  // pedirTopProductos: si el widget no trae selección propia y el usuario es
  // clínica matriz, mandan las clínicas de la matriz.
  const clinicasEfectivas = (widget) => {
    const delWidget = widget?.clientesSeleccionados?.map(c => c.id) || [];
    if (delWidget.length > 0) return delWidget;
    if (isClient && esClinicaMatriz) return clientesMatrizSeleccionados.map(c => c.id);
    return [];
  };

  // PLAN B PARA BACKENDS ANTIGUOS.
  //
  // El backend nuevo acepta `limite=0` y devuelve el listado completo. El
  // antiguo ignora ese parámetro y responde SIEMPRE su TOP 20, con lo que el
  // Excel salía recortado por muchas clínicas que se pidieran.
  //
  // Aquí se le da la vuelta: en vez de una petición con las N clínicas, se hace
  // UNA POR CLÍNICA. Cada una aporta su propio top y luego se fusiona sumando
  // los agregados. Con 38 clínicas eso pasó de 20 productos a 234.
  //
  // No es equivalente a quitar el límite: un producto que quede fuera del top
  // de TODAS las clínicas sigue sin aparecer. Es un apaño hasta que el backend
  // de producción entienda `limite`, no un sustituto.
  // Tramos mensuales para trocear una consulta que sigue tocando techo. El
  // endpoint acepta fecha_desde/fecha_hasta, así que cada mes trae su propio
  // TOP 20 y entre todos sale el listado real. Si el usuario ya filtró por
  // fechas se respeta su rango; si no, se barren los últimos 4 años.
  const tramosMensuales = () => {
    const hoy = new Date();
    const ini = fechaDesde ? new Date(fechaDesde) : new Date(hoy.getFullYear() - 4, 0, 1);
    const fin = fechaHasta ? new Date(fechaHasta) : hoy;
    const tramos = [];
    const cursor = new Date(ini.getFullYear(), ini.getMonth(), 1);
    while (cursor <= fin && tramos.length < 60) {
      const a = cursor.getFullYear(), m = cursor.getMonth() + 1;
      const ultimo = new Date(a, m, 0).getDate();
      tramos.push({
        desde: `${a}-${String(m).padStart(2, '0')}-01`,
        hasta: `${a}-${String(m).padStart(2, '0')}-${ultimo}`,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return tramos;
  };

  // Acumula una lista de productos sobre un Map, sumando los agregados.
  const acumular = (mapa, lista) => {
    for (const p of lista) {
      const clave = `${p.producto_id}-${p.proveedor_id}`;
      const acc = mapa.get(clave);
      if (!acc) { mapa.set(clave, { ...p }); continue; }
      acc.total_vendido      = (Number(acc.total_vendido)      || 0) + (Number(p.total_vendido)      || 0);
      acc.numero_pedidos     = (Number(acc.numero_pedidos)     || 0) + (Number(p.numero_pedidos)     || 0);
      acc.clientes_distintos = (Number(acc.clientes_distintos) || 0) + (Number(p.clientes_distintos) || 0);
      acc.facturacion_total  = (Number(acc.facturacion_total)  || 0) + (Number(p.facturacion_total)  || 0);
    }
  };

  const pedirProductosPorClinica = async (proveedorIds, clinicaIds) => {
    const mapa = new Map();
    const TAM_LOTE = 6; // en paralelo, pero sin abrir 38 conexiones de golpe

    for (let i = 0; i < clinicaIds.length; i += TAM_LOTE) {
      const lote = clinicaIds.slice(i, i + TAM_LOTE);
      const respuestas = await Promise.all(
        lote.map(id => pedirTopProductos(proveedorIds, [id], 0).catch(() => []))
      );
      // Una clínica que devuelve justo el tope sigue recortada: se trocea por
      // meses. Solo esas, para no disparar el número de peticiones.
      for (let j = 0; j < respuestas.length; j++) {
        if (respuestas[j].length !== TOPE_POR_DEFECTO_BACKEND) continue;
        const porMes = new Map();
        for (const t of tramosMensuales()) {
          const filas = await pedirTopProductos(proveedorIds, [lote[j]], 0, t).catch(() => []);
          acumular(porMes, filas);
        }
        if (porMes.size > respuestas[j].length) respuestas[j] = [...porMes.values()];
      }
      for (const lista of respuestas) acumular(mapa, lista);
    }

    return [...mapa.values()]
      .map(p => {
        const cantidad = Number(p.total_vendido) || 0;
        const facturacion = Number(p.facturacion_total) || 0;
        return { ...p, precio_promedio: cantidad > 0 ? facturacion / cantidad : Number(p.precio_promedio) || 0 };
      })
      .sort((a, b) => (Number(b.total_vendido) || 0) - (Number(a.total_vendido) || 0));
  };

  // proveedorIds: array de ids. Vacío = TOP global de todos los proveedores
  // visibles para el usuario (el backend agrupa y desglosa por proveedor).
  const fetchTopProductosProveedor = async (widgetId, proveedorIds = [], clienteIds = []) => {
    setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: true } : w));

    try {
      const productos = await pedirTopProductos(proveedorIds, clienteIds);
      console.log('✅ Top productos obtenidos:', productos.length);
      setTopProductos(prev => ({ ...prev, [widgetId]: productos }));
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: false } : w));
    }
  };

  const fetchBuscarProductos = async (widgetId, busqueda, proveedorIds, clienteIds = []) => {
    if (!busqueda || busqueda.length < 3) {
      setProductosComparativa(prev => ({ ...prev, [widgetId]: [] }));
      return;
    }

    setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: true } : w));

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('q', busqueda);
      
      if (isProveedor && userInfo?.proveedor_info) {
        params.append('proveedores', userInfo.proveedor_info.id);
      } else if (proveedorIds.length > 0) {
        params.append('proveedores', proveedorIds.join(','));
      }
      
      if (estadoFiltro) params.append('estado', estadoFiltro);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      
      if (isClient && esClinicaMatriz && clientesMatrizSeleccionados.length > 0 && clienteIds.length === 0) {
        const clientesIds = clientesMatrizSeleccionados.map(c => c.id).join(',');
        params.append('clientes_ids', clientesIds);
      } else if (clienteIds.length > 0) {
        params.append('clientes_ids', clienteIds.join(','));
      }

      const response = await fetch(`${API_URL}/api/analisis/buscar-productos?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al buscar productos');
      const data = await response.json();
      setProductosComparativa(prev => ({ ...prev, [widgetId]: data.productos || [] }));
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, isLoading: false } : w));
    }
  };

  const updateWidgetData = async (widgetId) => {
    if (isUpdatingWidget) return;
    
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return;

    if (isProveedor && userInfo?.proveedor_info) {
      if (widget.proveedoresSeleccionados.length === 0) {
        const proveedorPropio = {
          id: userInfo.proveedor_info.id,
          nombre: userInfo.proveedor_info.nombre,
          totalCompras: 0,
          numeroPedidos: 0
        };
        setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, proveedoresSeleccionados: [proveedorPropio] } : w));
        return;
      }

      const clienteIds = widget.clientesSeleccionados?.map(c => c.id) || [];

      if (widget.metricType === 'productos') {
        if (busquedaProducto && busquedaProducto.length >= 3) {
          await fetchBuscarProductos(widgetId, busquedaProducto, [userInfo.proveedor_info.id], clienteIds);
        } else {
          await fetchTopProductosProveedor(widgetId, [userInfo.proveedor_info.id], clienteIds);
        }
      } else {
        if (widget.tipoEje === 'cliente' && clienteIds.length > 1) {
          await fetchDatosTemporalesClientes(widgetId, clienteIds, [userInfo.proveedor_info.id]);
        } else {
          await fetchDatosTemporalesComparativa(widgetId, [userInfo.proveedor_info.id], clienteIds);
        }
      }
      return;
    }

    if (isClient && !esClinicaMatriz && userInfo?.clinica_info) {
      if (widget.clientesSeleccionados.length === 0) {
        const clientePropio = {
          id: userInfo.clinica_info.id,
          razon_social: userInfo.clinica_info.razon_social,
          total_facturado: 0,
          pedidos_activos: 0
        };
        setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, clientesSeleccionados: [clientePropio] } : w));
        return;
      }

      // En modo Productos, 0 proveedores significa "todos" (TOP global)
      if (widget.proveedoresSeleccionados.length === 0 && widget.metricType !== 'productos') {
        setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, data: [] } : w));
        return;
      }

      const proveedorIds = widget.proveedoresSeleccionados.map(p => p.id);
      const clienteIds = [userInfo.clinica_info.id];

      if (widget.metricType === 'productos') {
        if (busquedaProducto && busquedaProducto.length >= 3) {
          await fetchBuscarProductos(widgetId, busquedaProducto, proveedorIds, clienteIds);
        } else {
          await fetchTopProductosProveedor(widgetId, proveedorIds, clienteIds);
        }
      } else {
        await fetchDatosTemporalesComparativa(widgetId, proveedorIds, clienteIds);
      }
      return;
    }

    if (isClient && esClinicaMatriz) {
      // En modo Productos, 0 proveedores significa "todos" (TOP global)
      if (widget.proveedoresSeleccionados.length === 0 && widget.metricType !== 'productos') {
        setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, data: [] } : w));
        return;
      }

      const clienteIds = widget.clientesSeleccionados?.length > 0 ? 
        widget.clientesSeleccionados.map(c => c.id) : 
        clientesMatrizSeleccionados.map(c => c.id);
      
      const proveedorIds = widget.proveedoresSeleccionados.map(p => p.id);

      console.log('🔧 Clínica Matriz updateWidgetData:', {
        widgetId,
        clienteIds: clienteIds.length,
        proveedorIds: proveedorIds.length,
        tipoEje: widget.tipoEje,
        metricType: widget.metricType
      });

      if (widget.metricType === 'productos') {
        if (busquedaProducto && busquedaProducto.length >= 3) {
          await fetchBuscarProductos(widgetId, busquedaProducto, proveedorIds, clienteIds);
        } else {
          await fetchTopProductosProveedor(widgetId, proveedorIds, clienteIds);
        }
      } else {
        if (widget.tipoEje === 'cliente' && widget.proveedoresSeleccionados.length === 1 && clienteIds.length > 1) {
          console.log('🔍 Clínica Matriz: Llamando análisis por cliente', { widgetId, clienteIds, proveedorIds });
          await fetchDatosTemporalesClientes(widgetId, clienteIds, proveedorIds);
        } else {
          console.log('🔍 Clínica Matriz: Llamando análisis por proveedor', { widgetId, proveedorIds, clienteIds });
          await fetchDatosTemporalesComparativa(widgetId, proveedorIds, clienteIds);
        }
      }
      return;
    }

    if (isAdmin) {
      // En modo Productos, 0 proveedores significa "todos" (TOP global)
      if (widget.proveedoresSeleccionados.length === 0 && widget.metricType !== 'productos') {
        setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, data: [] } : w));
        return;
      }

      const clienteIds = widget.clientesSeleccionados?.map(c => c.id) || [];
      const proveedorIds = widget.proveedoresSeleccionados.map(p => p.id);

      if (widget.metricType === 'productos') {
        if (busquedaProducto && busquedaProducto.length >= 3) {
          await fetchBuscarProductos(widgetId, busquedaProducto, proveedorIds, clienteIds);
        } else {
          await fetchTopProductosProveedor(widgetId, proveedorIds, clienteIds);
        }
      } else {
        if (widget.tipoEje === 'cliente' && widget.proveedoresSeleccionados.length === 1 && clienteIds.length > 1) {
          console.log('🔍 Admin: Llamando análisis por cliente', { widgetId, clienteIds, proveedorIds });
          await fetchDatosTemporalesClientes(widgetId, clienteIds, proveedorIds);
        } else {
          console.log('🔍 Admin: Llamando análisis por proveedor', { widgetId, proveedorIds, clienteIds });
          await fetchDatosTemporalesComparativa(widgetId, proveedorIds, clienteIds);
        }
      }
      return;
    }
  };

  // `productosOverride` solo aplica a widgets de tipo 'productos'; ver
  // getDatosProductos.
  const getDatosComparativa = (widgetId, productosOverride) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return [];

    console.log('📊 getDatosComparativa', { 
      widgetId, 
      tipoEje: widget.tipoEje, 
      proveedores: widget.proveedoresSeleccionados?.length,
      clientes: widget.clientesSeleccionados?.length,
      metricType: widget.metricType
    });

    if (widget.metricType === 'productos') {
      return getDatosProductos(widgetId, productosOverride);
    }

    if (isProveedor) {
      if (widget.tipoEje === 'cliente' && widget.clientesSeleccionados?.length > 1) {
        return getDatosComparativaClientes(widgetId);
      } else {
        return getDatosComparativaProveedores(widgetId);
      }
    }
    
    if (isClient && !esClinicaMatriz) {
      return getDatosComparativaProveedores(widgetId);
    }

    if (isAdmin || (isClient && esClinicaMatriz)) {
      if (widget.tipoEje === 'cliente' && widget.proveedoresSeleccionados?.length === 1 && widget.clientesSeleccionados?.length > 1) {
        return getDatosComparativaClientes(widgetId);
      } else {
        return getDatosComparativaProveedores(widgetId);
      }
    }

    return [];
  };

  const getDatosComparativaProveedores = (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return [];

    const datosWidget = datosTemporales[widgetId] || {};
    if (Object.keys(datosWidget).length === 0) {
      console.log('❌ No hay datos en datosTemporales para widget', widgetId);
      return [];
    }
    
    console.log('📊 getDatosComparativaProveedores datos disponibles:', Object.keys(datosWidget));
    
    const todosLosMeses = new Set();
    
    if (isProveedor && userInfo?.proveedor_info) {
      const datosProveedor = datosWidget[userInfo.proveedor_info.id] || [];
      datosProveedor.forEach(dato => {
        todosLosMeses.add(dato.mes);
      });
    } else if (isClient && !esClinicaMatriz && userInfo?.clinica_info) {
      widget.proveedoresSeleccionados.forEach(proveedor => {
        const datosProveedor = datosWidget[proveedor.id] || [];
        datosProveedor.forEach(dato => {
          todosLosMeses.add(dato.mes);
        });
      });
    } else if (isClient && esClinicaMatriz) {
      console.log('🏢 Procesando datos para clínica matriz');
      console.log('🏢 Proveedores seleccionados en widget:', widget.proveedoresSeleccionados.length);
      console.log('🏢 Datos disponibles por proveedor:', Object.keys(datosWidget));
      
      widget.proveedoresSeleccionados.forEach(proveedor => {
        const datosProveedor = datosWidget[proveedor.id] || [];
        console.log(`🏢 Proveedor ${proveedor.nombre} (${proveedor.id}): ${datosProveedor.length} meses`);
        datosProveedor.forEach(dato => {
          todosLosMeses.add(dato.mes);
        });
      });
    } else {
      widget.proveedoresSeleccionados.forEach(proveedor => {
        const datosProveedor = datosWidget[proveedor.id] || [];
        datosProveedor.forEach(dato => {
          todosLosMeses.add(dato.mes);
        });
      });
    }
    
    console.log('📊 Meses encontrados:', Array.from(todosLosMeses));
    
    if (todosLosMeses.size === 0) {
      console.log('❌ No se encontraron meses en los datos');
      return [];
    }
    
    const mesesOrdenados = Array.from(todosLosMeses).sort((a, b) => {
      const fechaA = new Date(a.replace(/(\w+) (\d+)/, '$2-$1-01'));
      const fechaB = new Date(b.replace(/(\w+) (\d+)/, '$2-$1-01'));
      return fechaA - fechaB;
    });
    
    const resultado = mesesOrdenados.map(mes => {
      const data = { mes };
      
      if (isProveedor && userInfo?.proveedor_info) {
        const datosProveedor = datosWidget[userInfo.proveedor_info.id] || [];
        const datoMes = datosProveedor.find(dato => dato.mes === mes);
        
        if (datoMes) {
          switch(widget.metricType) {
            case 'pedidos':
              data[userInfo.proveedor_info.nombre] = datoMes.pedidos;
              break;
            case 'articulos':
              data[userInfo.proveedor_info.nombre] = datoMes.articulos;
              break;
            default:
              data[userInfo.proveedor_info.nombre] = datoMes.total;
              break;
          }
        } else {
          data[userInfo.proveedor_info.nombre] = 0;
        }
      } else {
        widget.proveedoresSeleccionados.forEach((proveedor) => {
          const datosProveedor = datosWidget[proveedor.id] || [];
          const datoMes = datosProveedor.find(dato => dato.mes === mes);
          
          if (datoMes) {
            switch(widget.metricType) {
              case 'pedidos':
                data[proveedor.nombre] = datoMes.pedidos;
                break;
              case 'articulos':
                data[proveedor.nombre] = datoMes.articulos;
                break;
              default:
                data[proveedor.nombre] = datoMes.total;
                break;
            }
          } else {
            data[proveedor.nombre] = 0;
          }
        });
      }
      
      return data;
    });
    
    console.log('📊 Resultado final getDatosComparativaProveedores:', resultado.length, 'meses');
    if (resultado.length > 0) {
      console.log('📊 Primer mes como ejemplo:', resultado[0]);
    }
    
    return resultado;
  };

  const getDatosComparativaClientes = (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return [];

    const datosWidget = datosTemporalesClientes[widgetId] || {};
    if (Object.keys(datosWidget).length === 0) return [];
    
    const todosLosMeses = new Set();
    widget.clientesSeleccionados.forEach(cliente => {
      const datosCliente = datosWidget[cliente.id] || [];
      datosCliente.forEach(dato => {
        todosLosMeses.add(dato.mes);
      });
    });
    
    const mesesOrdenados = Array.from(todosLosMeses).sort((a, b) => {
      const fechaA = new Date(a.replace(/(\w+) (\d+)/, '$2-$1-01'));
      const fechaB = new Date(b.replace(/(\w+) (\d+)/, '$2-$1-01'));
      return fechaA - fechaB;
    });
    
    return mesesOrdenados.map(mes => {
      const data = { mes };
      
      widget.clientesSeleccionados.forEach((cliente) => {
        const datosCliente = datosWidget[cliente.id] || [];
        const datoMes = datosCliente.find(dato => dato.mes === mes);
        
        if (datoMes) {
          switch(widget.metricType) {
            case 'pedidos':
              data[cliente.razon_social] = datoMes.pedidos;
              break;
            case 'articulos':
              data[cliente.razon_social] = datoMes.articulos;
              break;
            default:
              data[cliente.razon_social] = datoMes.total;
              break;
          }
        } else {
          data[cliente.razon_social] = 0;
        }
      });
      
      return data;
    });
  };

  // `productosOverride`: lista ya cargada que sustituye al estado del widget.
  // La usa la exportación a Excel, que pide el listado COMPLETO en vez del TOP
  // 20 que guarda el widget para pintar el gráfico.
  const getDatosProductos = (widgetId, productosOverride) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return [];

    // La fuente depende de lo que pidió el usuario, no de su rol: con búsqueda
    // activa se comparan proveedores para ese producto; sin ella, TOP 20.
    const hayBusqueda = busquedaProducto && busquedaProducto.length >= 3;
    const productos = productosOverride !== undefined ? productosOverride : (hayBusqueda ?
      (productosComparativa[widgetId] || []) : (topProductos[widgetId] || []));

    if (!productos || productos.length === 0) return [];

    // Con varios proveedores en el resultado, el nombre del producto solo no
    // identifica la fila (dos proveedores pueden vender el mismo artículo).
    const variosProveedores = new Set(productos.map(p => p.proveedor_id)).size > 1;

    return productos.map((producto, index) => {
      const etiqueta = variosProveedores && producto.proveedor_nombre ?
        `${producto.producto_nombre} · ${producto.proveedor_nombre}` :
        producto.producto_nombre;

      return {
        nombre: etiqueta.length > 20 ?
          etiqueta.substring(0, 20) + '...' :
          etiqueta,
        nombreCompleto: etiqueta,
        refProveedor: producto.ref_proveedor,
        marca: producto.marca,
        familia: producto.familia,
        proveedor: producto.proveedor_nombre,
        cliente: producto.cliente_nombre,
        totalVendido: parseInt(producto.total_vendido),
        numeroPedidos: parseInt(producto.numero_pedidos),
        clientesDistintos: parseInt(producto.clientes_distintos),
        facturacionTotal: parseFloat(producto.facturacion_total),
        precioPromedio: parseFloat(producto.precio_promedio),
        color: getProveedorColor(index),
        fill: getProveedorColor(index)
      };
    });
  };

  // Todo lo que necesita el debounce se lee desde este ref, que se refresca en
  // cada render. Si en su lugar se declara como dependencia del useCallback, la
  // función se recrea con cada setWidgets, el useEffect de abajo la vuelve a
  // disparar, el fetch actualiza widgets otra vez... y la tarjeta se queda
  // recargando en bucle infinito.
  const busquedaCtxRef = useRef(null);
  busquedaCtxRef.current = {
    widgets,
    selectedWidget,
    fetchBuscarProductos,
    fetchTopProductosProveedor
  };

  const debouncedSearchProductos = useCallback(
    debounce((busqueda) => {
      const ctx = busquedaCtxRef.current;
      if (!ctx) return;

      // La tarjeta seleccionada ya la refresca updateWidgetData al cambiar
      // busquedaProducto; incluirla aquí duplicaría la petición.
      const pendientes = ctx.widgets.filter(
        w => w.metricType === 'productos' && w.id !== ctx.selectedWidget
      );

      pendientes.forEach((widget, i) => {
        setTimeout(() => {
          const proveedorIds = widget.proveedoresSeleccionados.map(p => p.id);
          const clienteIds = widget.clientesSeleccionados?.map(c => c.id) || [];
          // Al borrar la búsqueda hay que volver al TOP, no dejar la comparativa vieja
          if (busqueda.length >= 3) {
            ctx.fetchBuscarProductos(widget.id, busqueda, proveedorIds, clienteIds);
          } else {
            ctx.fetchTopProductosProveedor(widget.id, proveedorIds, clienteIds);
          }
        }, i * 400);
      });
    }, 1000),
    []
  );

  // En el primer render no hay búsqueda que propagar: la carga inicial de cada
  // tarjeta la hace updateWidgetData.
  const busquedaMontadaRef = useRef(false);
  useEffect(() => {
    if (!busquedaMontadaRef.current) {
      busquedaMontadaRef.current = true;
      return;
    }
    debouncedSearchProductos(busquedaProducto);
  }, [busquedaProducto, debouncedSearchProductos]);

  useEffect(() => {
    console.log('🔄 useEffect: Actualizando datos de widgets');
    
    widgets.forEach(widget => {
      const datos = getDatosComparativa(widget.id);
      console.log(`📊 Widget ${widget.id} - datos obtenidos:`, datos.length, 'elementos');
      
      if (datos.length > 0) {
        console.log(`✅ Widget ${widget.id} - primer elemento:`, datos[0]);
      } else {
        console.log(`❌ Widget ${widget.id} - sin datos`);
      }
      
      setWidgets(prev => prev.map(w => w.id === widget.id ? { ...w, data: datos } : w));
    });
  }, [datosTemporales, datosTemporalesClientes, topProductos, productosComparativa]);

  useEffect(() => {
    if (selectedWidget) {
      const widget = widgets.find(w => w.id === selectedWidget);
      if (widget) {
        console.log('🔄 TRIGGER updateWidgetData para widget:', selectedWidget);
        console.log('🔄 Proveedores:', widget.proveedoresSeleccionados?.length || 0);
        console.log('🔄 Clientes:', widget.clientesSeleccionados?.length || 0);
        console.log('🔄 esClinicaMatriz:', esClinicaMatriz);
        console.log('🔄 clientesMatrizSeleccionados:', clientesMatrizSeleccionados.length);
        
        const timer = setTimeout(() => {
          console.log('🔄 EJECUTANDO updateWidgetData...');
          updateWidgetData(selectedWidget);
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
  }, [
    selectedWidget,
    widgets.find(w => w.id === selectedWidget)?.proveedoresSeleccionados,
    widgets.find(w => w.id === selectedWidget)?.clientesSeleccionados,
    widgets.find(w => w.id === selectedWidget)?.metricType,
    widgets.find(w => w.id === selectedWidget)?.chartType,
    widgets.find(w => w.id === selectedWidget)?.submetricaProductos,
    widgets.find(w => w.id === selectedWidget)?.tipoEje,
    busquedaProducto
  ]);

  const addWidget = (type = 'line') => {
    const typeNames = {
      'line': 'Evolución Temporal',
      'bar': 'Comparativa por Barras',
      'pie': 'Distribución Circular',
      'area': 'Área Acumulada'
    };

    const newWidget = {
      id: nextWidgetId,
      title: `${typeNames[type]} ${nextWidgetId}`,
      chartType: type,
      metricType: 'total',
      submetricaProductos: 'total_vendido',
      position: { 
        x: 50 + (nextWidgetId * 30), 
        y: 50 + (nextWidgetId * 30) 
      },
      size: { width: 500, height: 350 },
      data: [],
      proveedoresSeleccionados: [],
      clientesSeleccionados: [],
      tipoAnalisis: 'total',
      isLoading: false
    };

    if (isProveedor && userInfo?.proveedor_info) {
      newWidget.proveedoresSeleccionados = [{
        id: userInfo.proveedor_info.id,
        nombre: userInfo.proveedor_info.nombre,
        totalCompras: 0,
        numeroPedidos: 0
      }];
    } else if (isClient && userInfo?.clinica_info) {
      newWidget.clientesSeleccionados = [{
        id: userInfo.clinica_info.id,
        razon_social: userInfo.clinica_info.razon_social,
        total_facturado: 0,
        pedidos_activos: 0
      }];
    }
    
    setWidgets(prev => [...prev, newWidget]);
    setNextWidgetId(prev => prev + 1);
    setSelectedWidget(newWidget.id);
  };

  const removeWidget = (id) => {
    setWidgets(prev => prev.filter(w => w.id !== id));
    if (selectedWidget === id) {
      setSelectedWidget(widgets.length > 1 ? widgets.find(w => w.id !== id).id : null);
    }
  };

  const duplicateWidget = (id) => {
    const widgetToDuplicate = widgets.find(w => w.id === id);
    if (widgetToDuplicate) {
      const newWidget = {
        ...widgetToDuplicate,
        id: nextWidgetId,
        title: `${widgetToDuplicate.title} (Copia)`,
        position: {
          x: widgetToDuplicate.position.x + 20,
          y: widgetToDuplicate.position.y + 20
        },
        data: [],
        isLoading: false
      };
      setWidgets(prev => [...prev, newWidget]);
      setNextWidgetId(prev => prev + 1);
      setSelectedWidget(newWidget.id);
    }
  };

  const moveWidget = (id, newPosition) => {
    setWidgets(prev => 
      prev.map(w => w.id === id ? { ...w, position: newPosition } : w)
    );
  };

  const resizeWidget = (id, newSize) => {
    setWidgets(prev => 
      prev.map(w => w.id === id ? { ...w, size: newSize } : w)
    );
  };

  const selectWidget = (id) => {
    setSelectedWidget(id);
  };

  const updateWidget = (id, updates) => {
    setWidgets(prev => {
      const newWidgets = prev.map(w => w.id === id ? { ...w, ...updates } : w);
      return newWidgets;
    });
  };

  const updateWidgetTitle = (id, newTitle) => {
    setWidgets(prev => 
      prev.map(w => w.id === id ? { ...w, title: newTitle } : w)
    );
  };

  const exportCanvasAsImage = async () => {
    try {
      const html2canvas = window.html2canvas;
      
      if (!html2canvas) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = () => {
          exportCanvasWithHtml2Canvas();
        };
        script.onerror = () => {
          exportCanvasFallback();
        };
        document.head.appendChild(script);
        return;
      }
      
      exportCanvasWithHtml2Canvas();
      
    } catch (error) {
      console.error('Error al exportar imagen:', error);
      exportCanvasFallback();
    }
  };

  const exportCanvasWithHtml2Canvas = async () => {
    try {
      const html2canvas = window.html2canvas;
      
      if (html2canvas) {
        const canvas = await html2canvas(canvasRef.current, {
          backgroundColor: '#f9fafb',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          width: canvasRef.current.scrollWidth,
          height: canvasRef.current.scrollHeight,
          scrollX: 0,
          scrollY: 0
        });
        
        const link = document.createElement('a');
        link.download = `dashboard-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        exportCanvasFallback();
      }
    } catch (error) {
      console.error('Error con html2canvas:', error);
      exportCanvasFallback();
    }
  };

  const exportCanvasFallback = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = 1200;
      canvas.height = 800;
      
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#f9fafb');
      gradient.addColorStop(1, '#f3f4f6');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#111827';
      ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Dashboard Modular Completo', canvas.width/2, 60);
      
      ctx.fillStyle = '#6b7280';
      ctx.font = '16px system-ui, -apple-system, sans-serif';
      ctx.fillText(`${widgets.length} widgets configurados`, canvas.width/2, 90);
      ctx.fillText(`Exportado el ${new Date().toLocaleDateString('es-ES')}`, canvas.width/2, 115);
      
      ctx.textAlign = 'left';
      ctx.fillStyle = '#374151';
      ctx.font = '14px system-ui, -apple-system, sans-serif';
      ctx.fillText('Widgets en el dashboard:', 50, 180);
      
      widgets.forEach((widget, index) => {
        const y = 210 + (index * 25);
        ctx.fillStyle = theme.primary;
        ctx.fillText('•', 70, y);
        ctx.fillStyle = '#374151';
        ctx.fillText(`${widget.title} (${widget.chartType} - ${widget.metricType})`, 90, y);
      });
      
      ctx.fillStyle = '#7c3aed';
      ctx.font = '12px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Para exportar la imagen completa del dashboard, instala html2canvas en tu proyecto', canvas.width/2, canvas.height - 40);
      ctx.fillText('Esta es una vista resumen del dashboard actual', canvas.width/2, canvas.height - 20);
      
      const link = document.createElement('a');
      link.download = `dashboard-resumen-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error en fallback de exportación:', error);
      setError('Error al exportar la imagen');
    }
  };

  const aplicarFiltros = async () => {
    setIsApplyingFilters(true);
    try {
      console.log('Iniciando aplicación de filtros...');
      console.log('Filtros actuales:', { estadoFiltro, fechaDesde, fechaHasta });
      console.log('Usuario es clínica matriz:', esClinicaMatriz);
      console.log('Clientes matriz seleccionados:', clientesMatrizSeleccionados.length);
      
      console.log('Cargando KPIs...');
      await fetchKpisGenerales();
      await sleep(300);
      
      console.log('Cargando matriz de proveedores...');
      await fetchMatrizProveedores();
      await sleep(300);

      if (isClient && esClinicaMatriz) {
        console.log('Recargando Mis Clínicas con filtros aplicados...');
        await fetchListaClientes();
        await sleep(300);
      } else if (isAdmin || isProveedor) {
        console.log('Cargando lista de clientes...');
        await fetchListaClientes();
        await sleep(300);
      }
      
      if (selectedWidget) {
        const widget = widgets.find(w => w.id === selectedWidget);
        if (widget) {
          console.log(`Actualizando widget seleccionado ${selectedWidget}...`);
          setIsUpdatingWidget(true);
          await updateWidgetData(selectedWidget);
          setIsUpdatingWidget(false);
        }
      }
      
      console.log('Filtros aplicados correctamente');
    } catch (err) {
      console.error('Error al aplicar filtros:', err);
      setError('Error al aplicar los filtros');
    } finally {
      setIsApplyingFilters(false);
      setIsUpdatingWidget(false);
    }
  };

  const exportCanvasAsExcel = async () => {
    if (widgets.length === 0) {
      setError('No hay tarjetas en el dashboard para exportar');
      return;
    }

    try {
      setIsExportingExcel(true);
      
      if (typeof window.ExcelJS !== 'undefined' && window.ExcelJS.Workbook) {
        await generateExcelFile();
        return;
      }
      
      console.log('Cargando ExcelJS...');
      await loadExcelJS();
      await generateExcelFile();
      
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      setError('Error al exportar Excel: ' + error.message);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const loadExcelJS = () => {
    return new Promise((resolve, reject) => {
      if (document.querySelector('script[src*="exceljs"]')) {
        setTimeout(() => {
          if (window.ExcelJS && window.ExcelJS.Workbook) {
            resolve();
          } else {
            reject(new Error('ExcelJS no se cargó correctamente'));
          }
        }, 1000);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/exceljs@4.3.0/dist/exceljs.min.js';
      script.async = true;
      
      script.onload = () => {
        console.log('ExcelJS cargado desde CDN');
        setTimeout(() => {
          if (window.ExcelJS && window.ExcelJS.Workbook) {
            resolve();
          } else {
            reject(new Error('ExcelJS no se inicializó correctamente'));
          }
        }, 500);
      };
      
      script.onerror = () => {
        reject(new Error('Error al cargar ExcelJS desde CDN'));
      };
      
      document.head.appendChild(script);
    });
  };

  const generateExcelFile = async () => {
    try {
      console.log('Iniciando generación de Excel...');
      
      if (!window.ExcelJS || !window.ExcelJS.Workbook) {
        throw new Error('ExcelJS no está disponible');
      }
      
      const workbook = new window.ExcelJS.Workbook();
      
      const fecha = new Date().toLocaleDateString('es-ES');
      const fechaHora = new Date().toLocaleString('es-ES');

      // Convertir theme.primary a ARGB para ExcelJS (FF + hex sin #)
      const themeArgb = 'FF' + (theme.primary || '#3cbf9a').replace('#', '').toUpperCase();
      const themeDarkArgb = 'FF' + (theme.primaryDark || '#2da080').replace('#', '').toUpperCase();

      const worksheet = workbook.addWorksheet('Dashboard Análisis');
      
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: themeArgb } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        }
      };

      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: themeDarkArgb } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        }
      };

      const subHeaderStyle = {
        font: { bold: true, color: { argb: themeDarkArgb } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F9F7' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        }
      };

      const dataStyle = {
        border: {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        },
        alignment: { vertical: 'middle' }
      };

      const currencyStyle = {
        numFmt: '#,##0.00 €',
        alignment: { horizontal: 'right' }
      };

      const numberStyle = {
        numFmt: '#,##0',
        alignment: { horizontal: 'right' }
      };

      let currentRow = 1;

      const titleRow = worksheet.getRow(currentRow);
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      titleRow.getCell(1).value = 'DASHBOARD DE ANÁLISIS - REPORTE COMPLETO';
      titleRow.getCell(1).style = titleStyle;
      titleRow.height = 30;
      currentRow += 2;

      const infoRow = worksheet.getRow(currentRow);
      infoRow.getCell(1).value = 'Información General:';
      infoRow.getCell(1).style = subHeaderStyle;
      currentRow++;

      const infoData = [
        ['Fecha de generación:', fechaHora],
        ['Usuario:', user?.nombrec || 'Usuario'],
        ['Tipo de usuario:', isAdmin ? 'Administrador' : isClient ? 'Cliente' : 'Proveedor'],
        ['Estado de pedidos:', estadoFiltro ? getEstadoTexto(estadoFiltro) : 'Todos'],
        ['Fecha desde:', fechaDesde || 'Sin filtro'],
        ['Fecha hasta:', fechaHasta || 'Sin filtro'],
        ['Total de tarjetas:', widgets.length]
      ];

      infoData.forEach(([label, value]) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = label;
        row.getCell(1).style = { font: { bold: true } };
        row.getCell(2).value = value;
        currentRow++;
      });

      currentRow += 2;

      for (let widgetIndex = 0; widgetIndex < widgets.length; widgetIndex++) {
        const widget = widgets[widgetIndex];
        
        const widgetHeaderRow = worksheet.getRow(currentRow);
        const tipoGrafica = {
          'line': 'Línea',
          'bar': 'Barras', 
          'pie': 'Circular',
          'area': 'Área'
        }[widget.chartType] || widget.chartType;

        const tipoMetrica = {
          'total': 'Facturación',
          'pedidos': 'Nº Pedidos',
          'articulos': 'Cantidades',
          'productos': 'Productos'
        }[widget.metricType] || widget.metricType;

        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        widgetHeaderRow.getCell(1).value = `${widget.title} (${tipoGrafica}) - ${tipoMetrica}`;
        widgetHeaderRow.getCell(1).style = titleStyle;
        widgetHeaderRow.height = 25;
        currentRow += 2;

        // El widget solo guarda el TOP 20: es lo que cabe en un gráfico de
        // barras sin volverse ilegible. Para el Excel eso se queda corto, así
        // que se vuelve a pedir el listado COMPLETO con los mismos filtros.
        //
        // La petición se hace en dos tiempos y se AUTOCORRIGE:
        //   1. `limite=0`. Con el backend actualizado, aquí acaba todo.
        //   2. Si vuelven exactamente TOPE_POR_DEFECTO_BACKEND filas y hay más
        //      de una clínica, es que el backend ignoró el parámetro (versión
        //      antigua) -> se reparte la consulta clínica a clínica.
        //
        // El día que producción entienda `limite`, el paso 2 deja de dispararse
        // solo: no hay nada que desmontar.
        //
        // Si todo falla se exporta lo que ya tenía el widget: peor fichero,
        // pero fichero.
        let productosCompletos;
        const hayBusquedaProducto = busquedaProducto && busquedaProducto.length >= 3;
        if (widget.metricType === 'productos' && !hayBusquedaProducto) {
          const proveedorIds = widget.proveedoresSeleccionados?.map(p => p.id) || [];
          const clinicaIds = clinicasEfectivas(widget);
          try {
            productosCompletos = await pedirTopProductos(proveedorIds, clinicaIds, 0);
            console.log(`📗 Excel · limite=0 devolvió ${productosCompletos.length} productos`);

            if (productosCompletos.length === TOPE_POR_DEFECTO_BACKEND && clinicaIds.length > 0) {
              console.warn(`⚠️ El backend ignora "limite" (devolvió justo ${TOPE_POR_DEFECTO_BACKEND}). Troceando la consulta...`);
              // Con varias clínicas se reparte por clínica (y cada clínica que
              // siga en el tope se trocea por meses). Con una sola no hay nada
              // que repartir, así que se va directo al troceado por meses.
              const troceado = await pedirProductosPorClinica(proveedorIds, clinicaIds);
              if (troceado.length > productosCompletos.length) {
                productosCompletos = troceado;
              }
              console.log(`📗 Excel · tras trocear: ${productosCompletos.length} productos`);
            }
          } catch (err) {
            console.error('No se pudo cargar el listado completo; se exporta el TOP del widget:', err);
          }
        }

        const widgetData = getDatosComparativa(widget.id, productosCompletos);

        if (!widgetData || widgetData.length === 0) {
          const noDataRow = worksheet.getRow(currentRow);
          noDataRow.getCell(1).value = 'No hay datos disponibles para esta tarjeta';
          noDataRow.getCell(1).style = { 
            font: { italic: true }, 
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF7F7' } } 
          };
          currentRow += 3;
          continue;
        }

        if (widget.metricType === 'productos') {
          currentRow = generateProductTable(worksheet, widgetData, widget, currentRow, headerStyle, dataStyle, currencyStyle, numberStyle);
        } else {
          currentRow = generateTemporalTable(worksheet, widgetData, widget, currentRow, headerStyle, dataStyle, currencyStyle, numberStyle);
        }
      }

      worksheet.columns.forEach((column, index) => {
        if (index === 0) column.width = 25;
        else if (index === 1) column.width = 20;
        else column.width = 15;
      });

      console.log('Generando archivo Excel...');

      const nombreArchivo = `Dashboard_Analisis_${fecha.replace(/\//g, '-')}_${new Date().getTime()}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      console.log('Excel generado exitosamente:', nombreArchivo);

    } catch (error) {
      console.error('Error al generar Excel:', error);
      throw error;
    }
  };

  const generateProductTable = (worksheet, data, widget, startRow, headerStyle, dataStyle, currencyStyle, numberStyle) => {
    let currentRow = startRow;

    const headers = ['Ref. Proveedor', 'Producto', 'Marca', 'Proveedor'];
    
    const metricHeader = {
      'total_vendido': 'Cantidad Vendida',
      'numero_pedidos': 'Nº Pedidos',
      'facturacion_total': 'Facturación Total',
      'clientes_distintos': 'Clientes Distintos'
    }[widget.submetricaProductos] || 'Valor';
    
    headers.push(metricHeader);

    const headerRow = worksheet.getRow(currentRow);
    headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.style = headerStyle;
    });
    headerRow.height = 20;
    currentRow++;

    data.forEach((producto, index) => {
      const dataRow = worksheet.getRow(currentRow);
      
      dataRow.getCell(1).value = producto.refProveedor || '';
      dataRow.getCell(2).value = producto.nombreCompleto || producto.nombre;
      dataRow.getCell(3).value = producto.marca || '';
      dataRow.getCell(4).value = producto.proveedor || '';

      let valor = 0;
      let isCurrency = false;

      switch(widget.submetricaProductos) {
        case 'facturacion_total':
          valor = producto.facturacionTotal || 0;
          isCurrency = true;
          break;
        case 'numero_pedidos':
          valor = producto.numeroPedidos || 0;
          break;
        case 'clientes_distintos':
          valor = producto.clientesDistintos || 0;
          break;
        default:
          valor = producto.totalVendido || 0;
      }

      dataRow.getCell(5).value = valor;

      for (let i = 1; i <= 5; i++) {
        const cell = dataRow.getCell(i);
        cell.style = { ...dataStyle };

        if (i === 5) {
          cell.style = { ...dataStyle, ...(isCurrency ? currencyStyle : numberStyle) };
        }
      }

      if (index % 2 === 0) {
        for (let i = 1; i <= 5; i++) {
          const cell = dataRow.getCell(i);
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF2F2F2' }
          };
        }
      }

      currentRow++;
    });

    return currentRow + 2;
  };

  const generateTemporalTable = (worksheet, data, widget, currentRow, headerStyle, dataStyle, currencyStyle, numberStyle) => {
    if (!data || data.length === 0) return currentRow + 2;

    const meses = data.map(item => item.mes).filter(Boolean);
    
    const series = [];
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'mes' && !series.includes(key)) {
          series.push(key);
        }
      });
    });

    if (series.length === 0) return currentRow + 2;

    const headers = [widget.tipoEje === 'cliente' ? 'Cliente' : 'Proveedor', ...meses];

    const headerRow = worksheet.getRow(currentRow);
    headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.style = headerStyle;
    });
    headerRow.height = 20;
    currentRow++;

    series.forEach((serie, serieIndex) => {
      const dataRow = worksheet.getRow(currentRow);
      
      dataRow.getCell(1).value = serie;
      
      meses.forEach((mes, mesIndex) => {
        const dataItem = data.find(d => d.mes === mes);
        const valor = dataItem ? (dataItem[serie] || 0) : 0;
        
        const cell = dataRow.getCell(mesIndex + 2);
        cell.value = valor;
        
        const isCurrency = widget.metricType === 'total';
        cell.style = { ...dataStyle, ...(isCurrency ? currencyStyle : numberStyle) };
      });

      dataRow.getCell(1).style = { ...dataStyle, font: { bold: true } };

      if (serieIndex % 2 === 0) {
        for (let i = 1; i <= headers.length; i++) {
          const cell = dataRow.getCell(i);
          if (!cell.fill) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF2F2F2' }
            };
          }
        }
      }

      currentRow++;
    });

    return currentRow + 2;
  };

  const getEstadoTexto = (estado) => {
    const estados = {
      '2': 'Pendiente',
      '3': 'En proceso', 
      '4': 'Enviado',
      '5': 'Cerrado'
    };
    return estados[estado] || 'Desconocido';
  };

  const fetchClientesMatrizAsociadosDirecto = async (userData) => {
    if (!userData?.clinica_info) {
      console.log('🔍 No hay información de clínica para el usuario');
      return;
    }
    
    try {
      setIsLoadingClientesMatriz(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('No hay token disponible para clientes matriz');
        setEsClinicaMatriz(false);
        setClientesAsociados([]);
        return { esMatriz: false, clientesAsociados: [] };
      }
      
      console.log('🔍 Consultando clientes matriz para clínica:', userData.clinica_info.id);
      
      const response = await fetch(`${API_URL}/api/analisis/clientes-matriz-asociados`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Token inválido o expirado para clientes matriz');
          throw new Error('Token no válido');
        }
        throw new Error(`Error al obtener clientes asociados: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('🏢 Respuesta clientes matriz:', data);
      
      setEsClinicaMatriz(data.esMatriz);
      setClientesAsociados(data.clientesAsociados || []);
      
      if (data.esMatriz && data.clientesAsociados?.length > 0) {
        const todosLosClientes = [
          { id: userData.clinica_info.id, razon_social: userData.clinica_info.razon_social + ' (Matriz)' },
          ...data.clientesAsociados
        ];
        setClientesMatrizSeleccionados(todosLosClientes);
        console.log('✅ Auto-seleccionando todas las clínicas:', todosLosClientes.length);
      }
      
      return data;
    } catch (err) {
      console.error('❌ Error en fetchClientesMatrizAsociadosDirecto:', err);
      if (err.message !== 'Token no válido') {
        setError(err.message);
      }
      return { esMatriz: false, clientesAsociados: [] };
    } finally {
      setIsLoadingClientesMatriz(false);
    }
  };

  const cargarDatosIniciales = async () => {
    try {
      setIsInitialLoading(true);
      console.log('🚀 Iniciando carga de datos...');
      
      console.log('👤 Obteniendo información del usuario...');
      const userData = await fetchUserInfo();
      await sleep(200);
      
      setUserInfo(userData);
      await sleep(100);
      
      if (userData?.permisos === 5 && userData?.clinica_info) {
        console.log('🏢 Verificando clínicas matriz...');
        await fetchClientesMatrizAsociadosDirecto(userData);
        await sleep(300);
        await sleep(500);
      }
      
      console.log('📊 Cargando KPIs...');
      await fetchKpisGenerales();
      await sleep(300);
      
      console.log('🏢 Cargando matriz de proveedores...');
      await fetchMatrizProveedores();
      await sleep(300);
      
      if (userData?.permisos === 1 || userData?.permisos === 4) {
        console.log('👥 Cargando lista de clientes...');
        await fetchListaClientes();
        await sleep(200);
      }

      console.log('💾 Cargando configuraciones guardadas...');
      await cargarConfiguraciones();
      
      console.log('✅ Carga inicial completada');
    } catch (err) {
      console.error('❌ Error al cargar datos iniciales:', err);
      setError('Error al cargar los datos iniciales');
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  useEffect(() => {
    if (esClinicaMatriz && clientesAsociados.length > 0) {
      autoSelectAllClinicasForMatriz();
    }
  }, [esClinicaMatriz, clientesAsociados, autoSelectAllClinicasForMatriz]);

  useEffect(() => {
    const keysToDelete = [];
    for (const [key] of apiCache) {
      if (key.includes('kpis_') || key.includes('matriz_proveedores_')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => apiCache.delete(key));
  }, [estadoFiltro, fechaDesde, fechaHasta]);

  if (!user || isInitialLoading) {
    return (
      <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-4">
            <div
              className="w-12 h-12 border-4 rounded-full animate-spin mx-auto"
              style={{
                borderColor: hexToRgba(theme.primary, 0.25),
                borderTopColor: theme.primary
              }}
            ></div>
            <TrendingUp className="w-6 h-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" style={{ color: theme.primary }} />
          </div>
          <p className="text-gray-600 font-medium">
            {!user ? 'Cargando información del usuario...' : 'Cargando dashboard...'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Optimizando conexiones a la base de datos
          </p>
        </div>
      </div>
    );
  }

  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current) {
      setSelectedWidget(null);
    }
  };

  return (
    <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      
      {/* Modal para guardar configuración */}
      <SaveConfigModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={guardarConfiguracion}
        isLoading={isSavingConfig}
      />
      
      {/* Panel Lateral Deslizable */}
      <SidePanel
        isOpen={sidePanelOpen}
        setIsOpen={setSidePanelOpen}
        matrizProveedores={matrizProveedores}
        listaClientes={listaClientes}
        selectedWidget={selectedWidget}
        widgets={widgets}
        updateWidget={updateWidget}
        busquedaProveedor={busquedaProveedor}
        setBusquedaProveedor={setBusquedaProveedor}
        busquedaCliente={busquedaCliente}
        setBusquedaCliente={setBusquedaCliente}
        getProveedorColor={getProveedorColor}
        estadoFiltro={estadoFiltro}
        setEstadoFiltro={setEstadoFiltro}
        fechaDesde={fechaDesde}
        setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta}
        setFechaHasta={setFechaHasta}
        aplicarFiltros={aplicarFiltros}
        formatCurrency={formatCurrency}
        busquedaProducto={busquedaProducto}
        setBusquedaProducto={setBusquedaProducto}
        isApplyingFilters={isApplyingFilters}
        user={user}
        setError={setError}
        esClinicaMatriz={esClinicaMatriz}
        clientesAsociados={clientesAsociados}
        clientesMatrizSeleccionados={clientesMatrizSeleccionados}
        setClientesMatrizSeleccionados={setClientesMatrizSeleccionados}
        isLoadingClientesMatriz={isLoadingClientesMatriz}
      />

      {/* Área Principal */}
      <div className="h-full flex flex-col" style={{ 
        marginLeft: sidePanelOpen ? '380px' : '0px',
        transition: 'margin-left 0.3s ease-in-out'
      }}>
        
        {/* Header Compacto */}
        <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: hexToRgba(theme.primary, 0.1) }}>
                <Grid size={20} style={{ color: theme.primary }} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">Dashboard de Análisis</h1>
                {(isApplyingFilters || isUpdatingWidget || isSavingConfig || isLoadingConfigs) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {isApplyingFilters ? 'Aplicando filtros...' : 
                     isSavingConfig ? 'Guardando configuración...' :
                     isLoadingConfigs ? 'Cargando configuraciones...' :
                     'Actualizando tarjeta seleccionada...'}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Selector de configuraciones guardadas */}
              <div className="relative">
                <select
                  value={configuracionSeleccionada}
                  onChange={handleConfiguracionChange}
                  disabled={isLoadingConfigs || isApplyingFilters}
                  className="flex items-center px-3 py-2 text-sm border rounded-lg transition-colors duration-200 disabled:opacity-50 min-w-[180px]"
                  style={{
                    backgroundColor: hexToRgba(theme.primary, 0.06),
                    color: theme.primaryDark,
                    borderColor: hexToRgba(theme.primary, 0.3)
                  }}
                  title="Cargar configuración guardada"
                >
                  <option value="">
                    {isLoadingConfigs ? 'Cargando...' : 'Seleccionar formato'}
                  </option>
                  {configuracionesGuardadas.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.nombre}
                    </option>
                  ))}
                </select>
              </div>
                            
              <div className="flex items-center space-x-2">
                <button
                  onClick={exportCanvasAsImage}
                  disabled={isApplyingFilters || isUpdatingWidget || isSavingConfig || isExportingExcel}
                  className="flex items-center px-3 py-2 text-sm bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50"
                  title="Exportar Dashboard como Imagen PNG"
                >
                  <Image size={16} className="mr-1" />
                  PNG
                </button>
                
                <button
                  onClick={exportCanvasAsExcel}
                  disabled={isApplyingFilters || isUpdatingWidget || isSavingConfig || isExportingExcel || widgets.length === 0}
                  className="flex items-center px-3 py-2 text-sm border rounded-lg transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: hexToRgba(theme.primary, 0.06),
                    color: theme.primaryDark,
                    borderColor: hexToRgba(theme.primary, 0.3)
                  }}
                  onMouseEnter={e => { if (!isExportingExcel) e.currentTarget.style.backgroundColor = hexToRgba(theme.primary, 0.12); }}
                  onMouseLeave={e => { if (!isExportingExcel) e.currentTarget.style.backgroundColor = hexToRgba(theme.primary, 0.06); }}
                  title="Exportar Dashboard como Excel"
                >
                  {isExportingExcel ? (
                    <Loader size={16} className="mr-1 animate-spin" />
                  ) : (
                    <FileSpreadsheet size={16} className="mr-1" />
                  )}
                  Excel
                </button>
              </div>
              
              <div className="w-px h-6 bg-gray-300 mx-2"></div>
              
              <button
                onClick={() => addWidget('line')}
                disabled={isApplyingFilters || isUpdatingWidget || isSavingConfig}
                className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200 disabled:opacity-50"
                title="Agregar Gráfica de Línea"
              >
                <Plus size={16} className="mr-1" />
                <Activity size={16} className="mr-1" />
                Línea
              </button>
              
              <button
                onClick={() => addWidget('bar')}
                disabled={isApplyingFilters || isUpdatingWidget || isSavingConfig}
                className="flex items-center px-3 py-2 text-sm border rounded-lg transition-colors duration-200 disabled:opacity-50"
                style={{
                  backgroundColor: hexToRgba(theme.primary, 0.06),
                  color: theme.primaryDark,
                  borderColor: hexToRgba(theme.primary, 0.3)
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = hexToRgba(theme.primary, 0.12)}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = hexToRgba(theme.primary, 0.06)}
                title="Agregar Gráfica de Barras"
              >
                <Plus size={16} className="mr-1" />
                <BarChart3 size={16} className="mr-1" />
                Barras
              </button>
              
              <button
                onClick={() => addWidget('pie')}
                disabled={isApplyingFilters || isUpdatingWidget || isSavingConfig}
                className="flex items-center px-3 py-2 text-sm bg-purple-50 text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors duration-200 disabled:opacity-50"
                title="Agregar Gráfica Circular"
              >
                <Plus size={16} className="mr-1" />
                <Target size={16} className="mr-1" />
                Circular
              </button>
              
              <button
                onClick={() => addWidget('area')}
                disabled={isApplyingFilters || isUpdatingWidget || isSavingConfig}
                className="flex items-center px-3 py-2 text-sm bg-orange-50 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors duration-200 disabled:opacity-50"
                title="Agregar Gráfica de Área"
              >
                <Plus size={16} className="mr-1" />
                <Layers size={16} className="mr-1" />
                Área
              </button>
            </div>
          </div>
        </div>

        {/* Canvas Scrolleable */}
        <div className="flex-1 overflow-auto">
          <div
            ref={canvasRef}
            className="relative min-h-full"
            onClick={handleCanvasClick}
            style={{
              minHeight: '150vh',
              width: '200vw',
              backgroundImage: `
                radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0),
                linear-gradient(0deg, rgba(0,0,0,0.02) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px, 20px 20px, 20px 20px'
            }}
          >
            
            {/* Renderizar widgets */}
            {widgets.map(widget => (
              <ChartWidget
                key={widget.id}
                id={widget.id}
                title={widget.title}
                data={widget.data}
                chartType={widget.chartType}
                metricType={widget.metricType}
                submetricaProductos={widget.submetricaProductos}
                position={widget.position}
                size={widget.size}
                onMove={moveWidget}
                onResize={resizeWidget}
                onRemove={removeWidget}
                onDuplicate={duplicateWidget}
                onUpdateTitle={updateWidgetTitle}
                formatCurrency={formatCurrency}
                formatNumber={formatNumber}
                getProveedorColor={getProveedorColor}
                proveedoresSeleccionados={widget.proveedoresSeleccionados}
                matrizProveedores={matrizProveedores}
                isSelected={selectedWidget === widget.id}
                onSelect={selectWidget}
                isLoading={widget.isLoading}
                clientesSeleccionados={widget.clientesSeleccionados || []}
                tipoAnalisis={widget.tipoAnalisis || 'total'}
                tipoEje={widget.tipoEje || 'proveedor'}
                user={user}
                esClinicaMatriz={esClinicaMatriz}
              />
            ))}

          </div>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="absolute top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 max-w-sm z-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-red-100 p-2 rounded-lg mr-3">
                  <X size={16} className="text-red-600" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-red-800">Error</h4>
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 ml-2"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        
      </div>

      {/* Botón flotante para guardar configuración */}
      <button
        onClick={() => {
          setSelectedWidget(null);
          setShowSaveModal(true);
        }}
        disabled={isSavingConfig || isApplyingFilters || widgets.length === 0}
        className="fixed bottom-6 right-6 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed z-50"
        style={{ backgroundColor: theme.primary }}
        onMouseEnter={e => { if (!isSavingConfig && !isApplyingFilters) e.currentTarget.style.backgroundColor = theme.primaryDark; }}
        onMouseLeave={e => { if (!isSavingConfig && !isApplyingFilters) e.currentTarget.style.backgroundColor = theme.primary; }}
        title="Guardar configuración del dashboard"
      >
        {isSavingConfig ? (
          <Loader size={24} className="animate-spin" />
        ) : (
          <Save size={24} />
        )}
      </button>
    </div>
  );
}